// GenieX — SEO Content Engine
// Single source of truth for all 50+ premium landing pages.
// Pages are rendered by PremiumLandingPage / LocationLandingPage templates
// driven from this file. No per-page JSX required.
//
// IMPORTANT: Keep `slug` strings stable — they are URL paths.

// -----------------------------------------------------------------------------
// PRODUCT NAVIGATION — Mega-dropdown groups (One Switch-style).
// -----------------------------------------------------------------------------

export const PRODUCT_GROUPS = [
  {
    slug: "smart-controls",
    label: "Smart Controls",
    blurb: "Switches, panels, scene controllers — the surface of intelligence.",
    items: [
      { slug: "dynamic-series", label: "Dynamic Series" },
      { slug: "smart-touch-panels", label: "Smart Touch Panels" },
      { slug: "smart-switches", label: "Smart Switches" },
      { slug: "scene-controllers", label: "Scene Controllers" },
      { slug: "wireless-remotes", label: "Wireless Remotes" },
      { slug: "retro-modules", label: "Retro Modules" },
    ],
  },
  {
    slug: "lighting-ambience",
    label: "Lighting & Ambience",
    blurb: "Dimmers, mood lights, LED strips — atmosphere on demand.",
    items: [
      { slug: "smart-dimmers", label: "Smart Dimmers" },
      { slug: "led-strip-control", label: "LED Strip Control" },
      { slug: "smart-bulbs", label: "Smart Bulbs" },
      { slug: "mood-lighting", label: "Mood Lighting" },
      { slug: "color-tunable-lighting", label: "Color Tunable Lighting" },
      { slug: "outdoor-garden-lighting", label: "Outdoor Garden Lighting" },
    ],
  },
  {
    slug: "curtain-blind-automation",
    label: "Curtain & Blind Automation",
    blurb: "Whisper-silent motors, scheduled scenes, daylight-aware drapes.",
    items: [
      { slug: "curtain-motors", label: "Curtain Motors" },
      { slug: "roller-blinds", label: "Roller Blinds" },
      { slug: "roman-blinds", label: "Roman Blinds" },
      { slug: "venetian-blinds", label: "Venetian Blinds" },
      { slug: "skylight-control", label: "Skylight Control" },
    ],
  },
  {
    slug: "security-safety",
    label: "Security & Safety",
    blurb: "Smart locks, CCTV, leak & gas sensors — protection that hides.",
    items: [
      { slug: "smart-locks", label: "Smart Locks" },
      { slug: "video-door-phones", label: "Video Door Phones" },
      { slug: "cctv-solutions", label: "CCTV Solutions" },
      { slug: "gas-leak-detection", label: "Gas Leak Detection" },
      { slug: "smoke-sensors", label: "Smoke Sensors" },
      { slug: "water-leak-detection", label: "Water Leak Detection" },
      { slug: "panic-buttons", label: "Panic Buttons" },
    ],
  },
  {
    slug: "sensors",
    label: "Sensors",
    blurb: "Motion, presence, air quality — the senses of a smart home.",
    items: [
      { slug: "motion-sensors", label: "Motion Sensors" },
      { slug: "presence-sensors", label: "Presence Sensors" },
      { slug: "door-window-sensors", label: "Door & Window Sensors" },
      { slug: "temperature-sensors", label: "Temperature Sensors" },
      { slug: "air-quality-sensors", label: "Air Quality Sensors" },
      { slug: "light-sensors", label: "Light Sensors" },
    ],
  },
  {
    slug: "appliances-daily-living",
    label: "Appliances & Daily Living",
    blurb: "AC, TV, geyser, vacuum — daily comforts, intelligently directed.",
    items: [
      { slug: "ac-control", label: "AC Control" },
      { slug: "tv-control", label: "TV Control" },
      { slug: "robot-vacuum", label: "Robot Vacuum" },
      { slug: "smart-geyser", label: "Smart Geyser" },
      { slug: "smart-plugs", label: "Smart Plugs" },
      { slug: "pet-feeders", label: "Pet Feeders" },
    ],
  },
  {
    slug: "outdoor-utility",
    label: "Outdoor & Utility Automation",
    blurb: "Gates, sprinklers, pools, barriers — the estate, simplified.",
    items: [
      { slug: "gate-automation", label: "Gate Automation" },
      { slug: "boom-barriers", label: "Boom Barriers" },
      { slug: "sprinkler-automation", label: "Sprinkler Automation" },
      { slug: "pool-control", label: "Pool Control" },
      { slug: "driveway-sensors", label: "Driveway Sensors" },
    ],
  },
];

// -----------------------------------------------------------------------------
// SOLUTION NAVIGATION — Mega-dropdown groups.
// -----------------------------------------------------------------------------

export const SOLUTION_GROUPS = [
  {
    slug: "by-space",
    label: "By Space",
    blurb: "Tailored to the way you live.",
    items: [
      { slug: "complete-home", label: "Complete Home" },
      { slug: "home-theatre", label: "Home Theatre" },
      { slug: "villas", label: "Villas" },
      { slug: "apartments", label: "Apartments" },
      { slug: "rental", label: "Rental" },
      { slug: "hotel", label: "Hotel" },
    ],
  },
  {
    slug: "by-need",
    label: "By Need",
    blurb: "Built around the outcomes that matter.",
    items: [
      { slug: "smart-living", label: "Smart Living" },
      { slug: "home-security", label: "Home Security" },
      { slug: "energy-saving", label: "Energy Saving" },
      { slug: "elderly-friendly", label: "Elderly-Friendly" },
      { slug: "child-safe", label: "Child-Safe" },
    ],
  },
  {
    slug: "for-professionals",
    label: "For Professionals",
    blurb: "Partner with us. Specify with confidence.",
    items: [
      { slug: "for-architects", label: "For Architects" },
      { slug: "for-interior-designers", label: "For Interior Designers" },
      { slug: "for-builders", label: "For Builders" },
      { slug: "for-homeowners", label: "For Homeowners" },
    ],
  },
];

// -----------------------------------------------------------------------------
// LOCATION NAVIGATION — Cities served.
// -----------------------------------------------------------------------------

export const LOCATION_ITEMS = [
  { slug: "hyderabad", label: "Hyderabad" },
  { slug: "warangal", label: "Warangal" },
  { slug: "karimnagar", label: "Karimnagar" },
  { slug: "nizamabad", label: "Nizamabad" },
  { slug: "khammam", label: "Khammam" },
  { slug: "vijayawada", label: "Vijayawada" },
  { slug: "visakhapatnam", label: "Visakhapatnam" },
  { slug: "guntur", label: "Guntur" },
  { slug: "tirupati", label: "Tirupati" },
  { slug: "nellore", label: "Nellore" },
  { slug: "kakinada", label: "Kakinada" },
  { slug: "rajahmundry", label: "Rajahmundry" },
  { slug: "kurnool", label: "Kurnool" },
  { slug: "anantapur", label: "Anantapur" },
  { slug: "amaravati", label: "Amaravati" },
];

// =============================================================================
// CONTENT TEMPLATES
// =============================================================================
//
// Each landing page object has the shape:
//   {
//     slug, title, eyebrow, tagline, intro,
//     icon (lucide name string),
//     accent (gradient class hint),
//     benefits: [{ icon, title, body }],
//     useCases: [{ title, body }],
//     whyGenieX: [{ icon, title, body }],
//     faqs: [{ q, a }],
//     keywords: "..."
//   }
//
// Authoring style: Apple/Tesla — short, declarative, premium. Indian context.

const WHY_GENIEX_DEFAULT = [
  { icon: "BadgeCheck", title: "Curated, world-class products", body: "Every component is hand-picked from global luxury suppliers and re-engineered for Indian voltage, climate and lifestyle." },
  { icon: "HardHat", title: "White-glove installation", body: "Certified technicians. Furniture-grade finish. Zero damage to your interiors — guaranteed in writing." },
  { icon: "ShieldCheck", title: "5-year warranty. Lifetime care.", body: "We are the only Indian brand offering a 5-year on-paper warranty, with same-week service across Telangana & Andhra." },
  { icon: "Sparkles", title: "Apple-grade design discipline", body: "If it does not feel beautiful from across the room, it does not ship. Period." },
];

// -----------------------------------------------------------------------------
// PRODUCT PAGES — 35+ pages.
// -----------------------------------------------------------------------------

export const PRODUCT_PAGES = {
  // ---------- SMART CONTROLS ----------
  "dynamic-series": {
    title: "Dynamic Series — The switch, reimagined",
    eyebrow: "The Flagship",
    tagline: "The switch, reimagined.",
    intro:
      "An interactive glass display where a light switch used to live. Time, weather, scenes, and quiet control — at a glance. Crafted from a single piece of tempered glass, milled aluminium and warm light.",
    icon: "PanelTop",
    accent: "amber",
    benefits: [
      { icon: "Sparkles", title: "Interactive display", body: "Live time, weather and your scenes — quietly visible at a glance, dark when you don't need them." },
      { icon: "Gem", title: "Architectural craft", body: "Tempered glass, precision-milled aluminium, leather and brass — designed to disappear into the wall." },
      { icon: "Layers", title: "Personalized scenes", body: "Relax. Movie. Goodnight. Away. Crafted to your lifestyle, one touch away." },
      { icon: "Smartphone", title: "App + voice + touch", body: "Three ways to control — choose the one that suits the moment." },
    ],
    useCases: [
      { title: "Master bedroom", body: "A single switch that turns into a 'Goodnight' scene at 11pm — curtains close, lights dim, AC drops to 23°C." },
      { title: "Living room", body: "Movie scene fades 80% of the lights, drops the projector screen, shuts the curtains and cools the room." },
      { title: "Entrance foyer", body: "Welcome scene lifts up automatically as your phone, watch or car arrives at the door." },
    ],
    faqs: [
      { q: "Does it work without internet?", a: "Yes. Dynamic Series runs on a local LAN failover. Your home keeps working even when WiFi is down." },
      { q: "Can I retrofit it into an existing home?", a: "Yes. The retro module hides behind your existing switch box — no civil work, no rewiring, installation in 1–2 days." },
      { q: "What is the warranty?", a: "5-year on-paper warranty with same-week service across Telangana and Andhra Pradesh." },
      { q: "Does it work with Alexa and Google Home?", a: "Natively. Also works with Apple Home via bridges and SmartThings." },
    ],
    keywords: "Dynamic Series, smart switch India, interactive switch, luxury smart switch, GenieX",
  },
  "smart-touch-panels": {
    title: "Smart Touch Panels",
    eyebrow: "Glass · Light · Effortless",
    tagline: "Glass. Light. Effortless control.",
    intro:
      "Edge-to-edge glass panels with capacitive touch and back-lit icons. Replace every switch in your home with a surface that is beautiful when on, invisible when off.",
    icon: "Tablet",
    accent: "cyan",
    benefits: [
      { icon: "Hand", title: "Capacitive touch", body: "Whisper-light touch response with haptic feedback." },
      { icon: "Sun", title: "Auto-dimming back-light", body: "Bright in the day, soft at night, dark when the room is empty." },
      { icon: "Palette", title: "Choose your finish", body: "Black, white, champagne gold, brushed steel and graphite — to match any palette." },
      { icon: "Layers", title: "Scene shortcuts", body: "Up to 8 customizable scene buttons per panel." },
    ],
    useCases: [
      { title: "Entry foyer", body: "A single 4-button panel for Welcome, Away, Goodnight and All Off." },
      { title: "Bedside", body: "Master and reading lights, curtains, AC, and 'Goodnight' — all from one panel within arm's reach." },
      { title: "Home theatre", body: "Lights, curtains, projector and audio on a discreet wall panel — no remote graveyard." },
    ],
    faqs: [
      { q: "Are they waterproof?", a: "IP44 rated — suitable for bathroom walls and outdoor verandas under cover." },
      { q: "Can I label the buttons?", a: "Yes — laser-engraved or back-printed labels in your choice of language." },
    ],
    keywords: "smart touch panels India, glass switches, GenieX touch panel",
  },
  "smart-switches": {
    title: "Smart Switches",
    eyebrow: "WiFi-native control",
    tagline: "Every switch in your home, suddenly intelligent.",
    intro:
      "Premium WiFi smart switches that replace your existing wiring. App, voice and physical control — without compromising the look of your wall.",
    icon: "ToggleRight",
    accent: "violet",
    benefits: [
      { icon: "Wifi", title: "WiFi + Local LAN", body: "Works on your home WiFi with local failover — never depends on the cloud." },
      { icon: "Mic", title: "Voice control", body: "Alexa, Google Home and Siri Shortcuts out of the box." },
      { icon: "Clock", title: "Schedules", body: "Lights, fans and geysers on a routine that suits your life." },
      { icon: "Smartphone", title: "App control", body: "Every switch in your home, in your pocket." },
    ],
    useCases: [
      { title: "First smart home upgrade", body: "Start with the living room and master bedroom — expand at your pace." },
      { title: "Rental upgrade", body: "Take it with you when you move — switches detach in minutes." },
    ],
    faqs: [
      { q: "Will it fit my existing box?", a: "Standard 1, 2, 4, 6 and 8-module sizes — fits Indian modular boxes." },
    ],
    keywords: "smart switches India, WiFi switches, GenieX",
  },
  "scene-controllers": {
    title: "Scene Controllers",
    eyebrow: "One touch · Whole house",
    tagline: "One touch. The whole room responds.",
    intro:
      "Programmable scene buttons that orchestrate light, climate, curtains, music and presence. The luxury of doing less.",
    icon: "LayoutGrid",
    accent: "amber",
    benefits: [
      { icon: "Sparkles", title: "Unlimited scenes", body: "Programmable per home — your scenes, named your way." },
      { icon: "Clock", title: "Time-aware", body: "The same button does different things at 7am vs 11pm — automatically." },
      { icon: "Users", title: "Per-person scenes", body: "Wife's morning. Husband's morning. Children's bedtime. Independently programmable." },
    ],
    useCases: [
      { title: "Wake up", body: "Curtains open 20%. Bedside lamp lifts gently to 40%. AC eases off. The house becomes 'awake'." },
      { title: "Dinner party", body: "One press. Dining warm-white. Music to playlist 'Evening'. Foyer lights to 30%. Service zone bright." },
    ],
    faqs: [
      { q: "How many scenes can I have?", a: "Unlimited. Most homes settle at 12–20 named scenes after a few weeks." },
    ],
    keywords: "scene controllers, smart home scenes, GenieX",
  },
  "wireless-remotes": {
    title: "Wireless Remotes",
    eyebrow: "Discreet · Battery · Anywhere",
    tagline: "Place a switch anywhere. Without wiring.",
    intro:
      "Battery-powered wireless switches that stick to glass, marble, wood — wherever you want a switch but never expected one.",
    icon: "Radio",
    accent: "violet",
    benefits: [
      { icon: "BatteryFull", title: "5-year battery", body: "Replace once a Olympiad." },
      { icon: "Magnet", title: "Magnetic mount", body: "Sticks where you need it, comes off when you don't." },
      { icon: "Wand2", title: "Programmable", body: "Single press, double press, long press — three actions per button." },
    ],
    useCases: [
      { title: "Bedside without rewiring", body: "Tape it to the headboard. Goodnight scene, finally within reach." },
      { title: "Sofa-side remote", body: "One remote for the entire living room — lights, AC, curtains, TV." },
    ],
    faqs: [
      { q: "Does it work without WiFi?", a: "Yes. Communicates directly with the GenieX hub over Zigbee/RF — no internet required." },
    ],
    keywords: "wireless smart remote, battery switches India, GenieX",
  },
  "retro-modules": {
    title: "Retro Modules",
    eyebrow: "Smart home, without rewiring",
    tagline: "Make your existing switches intelligent.",
    intro:
      "A discreet module that fits behind your current switches. Keeps your existing look. Adds app, voice and scene control overnight.",
    icon: "Cpu",
    accent: "cyan",
    benefits: [
      { icon: "HousePlug", title: "Zero civil work", body: "No rewiring, no broken walls, no paint repair." },
      { icon: "Clock", title: "Installed in 1–2 days", body: "Per BHK. Move-in-ready intelligence." },
      { icon: "Smartphone", title: "App + voice", body: "Adds full smart home capability to your old switches." },
      { icon: "RotateCcw", title: "Reversible", body: "Take it out, your home is exactly as it was." },
    ],
    useCases: [
      { title: "Rented apartment", body: "Smart home today. Take it with you when you move." },
      { title: "Heritage home", body: "Preserve the original switches. Add the intelligence behind them." },
    ],
    faqs: [
      { q: "Will my existing switches still work?", a: "Yes. Physical switches keep working — intelligence is added on top." },
    ],
    keywords: "retro smart home, no rewiring smart switch, GenieX retro module",
  },

  // ---------- LIGHTING & AMBIENCE ----------
  "smart-dimmers": {
    title: "Smart Dimmers",
    eyebrow: "Lutron-quality dimming",
    tagline: "Every light, a thousand moods.",
    intro:
      "Phase-cut and 0–10V dimmers tuned for chandeliers, cove lights and architectural lighting. Smooth as cinema. Engineered for Indian voltage.",
    icon: "SlidersHorizontal",
    accent: "amber",
    benefits: [
      { icon: "Sun", title: "Cinema-smooth", body: "Logarithmic curves that match how your eye perceives light." },
      { icon: "Zap", title: "Surge-protected", body: "Handles Indian voltage swings without flicker or chandelier hum." },
      { icon: "Layers", title: "Scenes per fixture", body: "Different rooms, different dimming behaviours, all in one app." },
    ],
    useCases: [
      { title: "Chandelier", body: "From a delicate 5% candlelit dinner glow to full 100% gala brightness." },
      { title: "Cove lighting", body: "Cool-white at noon. Warm-white at sunset. Amber whisper at midnight." },
    ],
    faqs: [
      { q: "Do they work with my existing bulbs?", a: "Yes — phase-cut for incandescent and dimmable LED, 0–10V for high-end architectural fixtures." },
    ],
    keywords: "smart dimmer India, LED dimmer, GenieX",
  },
  "led-strip-control": {
    title: "LED Strip Control",
    eyebrow: "RGBW · CCT · Pixel",
    tagline: "Light, painted onto your architecture.",
    intro:
      "Controllers for RGB, CCT and addressable pixel LED strips. Smooth scenes, music sync, and cove-lighting that knows the hour.",
    icon: "Lightbulb",
    accent: "violet",
    benefits: [
      { icon: "Palette", title: "16 million colours", body: "Real RGBW with white channel — not muddy pastels." },
      { icon: "Music", title: "Music sync", body: "Lights pulse to your playlist — for the moments that call for it." },
      { icon: "Clock", title: "Circadian", body: "Cool-white morning, warm-white evening — light that respects your body clock." },
    ],
    useCases: [
      { title: "Cove lighting", body: "Tunable white that shifts with the time of day, without you ever touching the app." },
      { title: "TV bias lighting", body: "Reduces eye strain during late-night viewing — subtle, gorgeous, automatic." },
    ],
    faqs: [{ q: "Pixel strips supported?", a: "Yes — WS2812B, WS2815 and SK6812." }],
    keywords: "LED strip controller, RGB controller India, GenieX",
  },
  "smart-bulbs": {
    title: "Smart Bulbs",
    eyebrow: "Bulbs, but smarter",
    tagline: "Replace one bulb. Discover what smart lighting can be.",
    intro:
      "E27 and B22 smart bulbs with tunable white, full colour and scenes — the gentlest possible entry into intelligent living.",
    icon: "Lightbulb",
    accent: "amber",
    benefits: [
      { icon: "Wifi", title: "WiFi-native", body: "No hub required. Just screw in and pair." },
      { icon: "Sun", title: "Tunable white", body: "2700K warm to 6500K daylight — at the tap of your phone." },
      { icon: "Mic", title: "Voice control", body: "Alexa, Google Home and Siri." },
    ],
    useCases: [
      { title: "First smart bulb", body: "Try one. You'll buy ten." },
    ],
    faqs: [{ q: "Lumen output?", a: "806 lm @ 9W — equivalent to a 60W incandescent." }],
    keywords: "smart bulbs India, WiFi bulb, GenieX",
  },
  "mood-lighting": {
    title: "Mood Lighting",
    eyebrow: "Scenes for living",
    tagline: "The light that matches the moment.",
    intro:
      "Pre-engineered scenes — Relax, Read, Dinner, Movie, Party, Goodnight — tuned by lighting designers, ready on day one.",
    icon: "Sparkles",
    accent: "violet",
    benefits: [
      { icon: "Heart", title: "Tuned by experts", body: "Scenes designed by professional lighting designers, not engineers." },
      { icon: "Layers", title: "Multi-fixture", body: "One scene orchestrates 20+ fixtures across multiple rooms." },
      { icon: "Hand", title: "One-touch recall", body: "From the panel, app, voice — or just a remote on the coffee table." },
    ],
    useCases: [
      { title: "Movie night", body: "Living room drops to 15%. Cove glows amber. TV bias lifts. Curtains close." },
    ],
    faqs: [{ q: "Can I edit the default scenes?", a: "Yes — every scene is editable, and we encourage tweaking until they feel like yours." }],
    keywords: "mood lighting India, smart lighting scenes, GenieX",
  },
  "color-tunable-lighting": {
    title: "Color Tunable Lighting",
    eyebrow: "Light that respects your body",
    tagline: "Daylight at noon. Candlelight at midnight.",
    intro:
      "Tunable-white luminaires that shift colour temperature through the day — improving sleep, mood and focus by mirroring natural light.",
    icon: "SunMoon",
    accent: "amber",
    benefits: [
      { icon: "Sun", title: "Circadian rhythm", body: "Cool-white morning, warm-white evening — automatic, every day." },
      { icon: "Brain", title: "Better sleep", body: "Studies show tunable lighting can improve sleep latency by up to 23%." },
      { icon: "Eye", title: "Reduced eye strain", body: "Lighting that matches the time of day reduces visual fatigue." },
    ],
    useCases: [
      { title: "Bedroom", body: "Wake up with cool morning light. Wind down with warm evening glow." },
      { title: "Home office", body: "Energising 5000K through the workday. Relaxing 2700K after 7pm." },
    ],
    faqs: [{ q: "What temperatures are supported?", a: "Full 2200K–6500K range with 1K precision." }],
    keywords: "tunable white lighting, circadian lighting India, GenieX",
  },
  "outdoor-garden-lighting": {
    title: "Outdoor Garden Lighting",
    eyebrow: "IP65 · Path · Spot · Wall",
    tagline: "A garden that comes alive at sunset.",
    intro:
      "IP65-rated outdoor smart lighting — path lights, spike lights, wall washers and water-feature lights that orchestrate as one.",
    icon: "Trees",
    accent: "cyan",
    benefits: [
      { icon: "ShieldCheck", title: "IP65 weatherproof", body: "Engineered for Indian monsoon and dust." },
      { icon: "Sun", title: "Astro-clock", body: "Knows your latitude — switches on at sunset, off at sunrise." },
      { icon: "Layers", title: "Garden scenes", body: "Path-only at 9pm. Full garden + water feature for parties." },
    ],
    useCases: [
      { title: "Villa garden", body: "Trees lit from below. Pathways glowing. Water feature shimmering. All automatic at dusk." },
    ],
    faqs: [{ q: "Solar option?", a: "Yes — solar-powered path lights for off-grid garden areas." }],
    keywords: "outdoor smart lighting, garden lights India, GenieX",
  },

  // ---------- CURTAIN & BLIND AUTOMATION ----------
  "curtain-motors": {
    title: "Curtain Motors",
    eyebrow: "Silent · Strong · Beautiful",
    tagline: "Drapery that knows the hour.",
    intro:
      "DC-quiet curtain motors with German bearings — engineered for heavy silk drapes up to 60kg. Whisper-silent operation under 25dB.",
    icon: "Wind",
    accent: "amber",
    benefits: [
      { icon: "VolumeX", title: "<25 dB", body: "Quieter than your refrigerator. You will not hear them open at 7am." },
      { icon: "Dumbbell", title: "Up to 60kg", body: "Handles the heaviest silk and velvet drapes in a Hyderabad villa." },
      { icon: "Sun", title: "Daylight-aware", body: "Opens to 40% at sunrise. Fully closes at sunset. You decide." },
    ],
    useCases: [
      { title: "Master bedroom", body: "Curtains rise gently as part of your 'Wake up' scene — no alarm needed." },
      { title: "Living room", body: "Close on 'Movie'. Open on 'Welcome'. One touch." },
    ],
    faqs: [
      { q: "Track or rod?", a: "Both — ceiling-track motors and decorative rod motors." },
      { q: "Will the rod look bulky?", a: "No — motors hide inside a 35mm rod, almost invisible." },
    ],
    keywords: "curtain motor India, motorised curtains, GenieX",
  },
  "roller-blinds": {
    title: "Roller Blinds",
    eyebrow: "Minimal · Modern · Motorized",
    tagline: "Light, dialled to a percentage.",
    intro:
      "Tubular motors for roller blinds in screen, blackout and translucent fabrics — designed for clean, contemporary interiors.",
    icon: "AlignHorizontalDistributeCenter",
    accent: "violet",
    benefits: [
      { icon: "Layers", title: "Three fabric tiers", body: "Screen (5% openness), translucent, full blackout." },
      { icon: "Sun", title: "Sun-tracking", body: "Lowers automatically when sun hits the window — protects art and rugs from fading." },
      { icon: "Smartphone", title: "Group control", body: "All bedroom blinds, all study blinds, all living-room blinds — independent groups." },
    ],
    useCases: [
      { title: "Home office", body: "Auto-lowers when glare hits your screen. Rises when the sun moves on." },
      { title: "Children's room", body: "Blackout drops to 100% at 8pm — bedtime, on schedule." },
    ],
    faqs: [{ q: "Custom sizes?", a: "Yes — manufactured to your window dimensions, no compromise." }],
    keywords: "motorised roller blinds India, smart blinds, GenieX",
  },
  "roman-blinds": {
    title: "Roman Blinds",
    eyebrow: "Classic, motorized",
    tagline: "The romance of fabric, the precision of motors.",
    intro:
      "Folded, fabric-rich Roman blinds with concealed lift-motors — for living rooms, dining areas and master suites.",
    icon: "Layers",
    accent: "amber",
    benefits: [
      { icon: "Gem", title: "Premium fabrics", body: "Linens, silks, velvets — designer-grade textures." },
      { icon: "VolumeX", title: "Silent lift", body: "Concealed lift-motors raise the blind in elegant folds." },
    ],
    useCases: [{ title: "Master bedroom", body: "Rises beautifully at sunrise. Folds away when you don't need it." }],
    faqs: [{ q: "Maintenance?", a: "Removable for dry cleaning — once a year." }],
    keywords: "motorised roman blinds, GenieX",
  },
  "venetian-blinds": {
    title: "Venetian Blinds",
    eyebrow: "Tilt + Lift, motorized",
    tagline: "Sunlight, sliced precisely.",
    intro:
      "Aluminium and timber Venetian blinds with motorised tilt and lift — directing daylight without losing the view.",
    icon: "AlignHorizontalSpaceAround",
    accent: "cyan",
    benefits: [
      { icon: "Sun", title: "Tilt-only control", body: "Direct sunlight without lowering the blind — keep the view, lose the glare." },
      { icon: "Trees", title: "Timber option", body: "Premium basswood for a warm, organic look." },
    ],
    useCases: [{ title: "Study", body: "Tilts to 45° as the afternoon sun rises — workspace remains glare-free." }],
    faqs: [{ q: "Slat sizes?", a: "25mm and 50mm aluminium, 50mm timber." }],
    keywords: "motorised venetian blinds, GenieX",
  },
  "skylight-control": {
    title: "Skylight Control",
    eyebrow: "Sun + Rain, intelligently",
    tagline: "The roof, that knows the weather.",
    intro:
      "Motorized skylights with rain sensors, sun-tracking shades and chain motors — for double-height living rooms and atriums.",
    icon: "CloudSun",
    accent: "amber",
    benefits: [
      { icon: "CloudRain", title: "Rain sensor", body: "Auto-closes the moment it senses moisture — protects floors and art." },
      { icon: "Sun", title: "Heat management", body: "Shade lowers automatically when interior temperature rises." },
    ],
    useCases: [{ title: "Atrium", body: "Skylight opens for ventilation. Closes the moment monsoon arrives." }],
    faqs: [{ q: "Power-failure safe?", a: "Yes — battery backup ensures it closes during power cuts." }],
    keywords: "motorised skylight, GenieX",
  },

  // ---------- SECURITY & SAFETY ----------
  "smart-locks": {
    title: "Smart Locks",
    eyebrow: "Six ways in. One peace of mind.",
    tagline: "The threshold, intelligently kept.",
    intro:
      "Face recognition, fingerprint, RFID, mobile, PIN and mechanical key — six independent ways into a modern home. Hospitality-grade reliability with a discreet, beautiful exterior.",
    icon: "Lock",
    accent: "amber",
    benefits: [
      { icon: "ScanFace", title: "3D face recognition", body: "Unlocks in 0.4 seconds, even at night — anti-spoof protected." },
      { icon: "Fingerprint", title: "Fingerprint reader", body: "Up to 100 unique prints. Family, staff, vendors — each with their own access." },
      { icon: "Smartphone", title: "Mobile key", body: "Send a one-time key over WhatsApp. Valid for 2 hours. Logged forever." },
      { icon: "History", title: "Access log", body: "Every entry, timestamped. See who, when, and how — from your phone." },
    ],
    useCases: [
      { title: "Working couple", body: "Maid's fingerprint works between 9am-1pm. Outside that window, the lock won't recognise her. No keys, no calls." },
      { title: "Air-bnb host", body: "Send the guest a mobile key 30 minutes before check-in. It auto-expires at checkout." },
    ],
    faqs: [
      { q: "What if the battery dies?", a: "USB-C emergency power port at the bottom — five minutes on a power bank gets you in. Plus, a mechanical key always works." },
      { q: "Indian door compatibility?", a: "Compatible with all standard Indian wooden doors, 35–110mm thickness. Steel doors included." },
    ],
    keywords: "smart locks India, face recognition lock, fingerprint lock, GenieX",
  },
  "video-door-phones": {
    title: "Video Door Phones",
    eyebrow: "See your door from anywhere",
    tagline: "See your door. From anywhere on Earth.",
    intro:
      "4K outdoor doorbell with anti-vandal alloy housing, 160° wide-angle view and AI-driven person/parcel detection.",
    icon: "DoorOpen",
    accent: "cyan",
    benefits: [
      { icon: "Camera", title: "4K UHD", body: "Crystal-clear day and night — IR + colour night vision." },
      { icon: "Bell", title: "Multi-device ring", body: "Rings on every phone, every panel and every Echo Show in the home." },
      { icon: "Bot", title: "AI detection", body: "Tells you 'person at door' vs 'package delivered' vs 'movement'." },
    ],
    useCases: [
      { title: "Travelling", body: "Answer your door from Singapore. Tell the courier where to leave the package." },
    ],
    faqs: [{ q: "Two-way audio?", a: "Yes — full duplex, with noise cancellation for traffic-side gates." }],
    keywords: "video doorbell India, smart door phone, GenieX",
  },
  "cctv-solutions": {
    title: "CCTV Solutions",
    eyebrow: "Seen. Recorded. Reassured.",
    tagline: "Surveillance that feels like architecture.",
    intro:
      "End-to-end CCTV — discreetly engineered into your home, with AI person/vehicle detection, 30-day cloud + on-site NVR, and a single elegant app.",
    icon: "Cctv",
    accent: "violet",
    benefits: [
      { icon: "Eye", title: "4K + 8MP", body: "Read a number plate from 40 feet away." },
      { icon: "Moon", title: "Colour night vision", body: "No more grainy black-and-white at 2am." },
      { icon: "Bot", title: "AI search", body: "'Show me every red car in last 48 hours' — and it does." },
      { icon: "Cloud", title: "30-day cloud", body: "On-site NVR + encrypted cloud — never lose the footage that matters." },
    ],
    useCases: [
      { title: "Villa perimeter", body: "8 cameras around the boundary — alerts only when a person, not a leaf, crosses the line." },
    ],
    faqs: [
      { q: "How many cameras can I install?", a: "Up to 64 channels on a single NVR — most homes need 8–16." },
      { q: "Is the cloud secure?", a: "AES-256 encrypted, India-hosted, GDPR + DPDP-compliant." },
    ],
    keywords: "home CCTV India, smart surveillance, GenieX",
  },
  "gas-leak-detection": {
    title: "Gas Leak Detection",
    eyebrow: "Safety, on standby",
    tagline: "Before a smell becomes a story.",
    intro:
      "LPG and PNG sensors that detect leaks within 0.3% concentration — and shut off the gas valve automatically.",
    icon: "Flame",
    accent: "amber",
    benefits: [
      { icon: "Zap", title: "Auto-shutoff valve", body: "When a leak is detected, the gas valve closes within 2 seconds." },
      { icon: "Bell", title: "Multi-channel alert", body: "Siren, app push, SMS, and call — until acknowledged." },
      { icon: "ShieldCheck", title: "BIS certified", body: "Tested to Indian safety standards." },
    ],
    useCases: [
      { title: "Modular kitchen", body: "Most leaks happen behind the hob. The sensor sees it first, every time." },
    ],
    faqs: [{ q: "Battery-backed?", a: "Yes — works through power cuts." }],
    keywords: "gas leak sensor India, kitchen safety, GenieX",
  },
  "smoke-sensors": {
    title: "Smoke Sensors",
    eyebrow: "Fire safety, residential-grade",
    tagline: "Quiet vigilance, audible alarm.",
    intro:
      "Photoelectric smoke detectors with interconnected alarms — when one triggers, every alarm in the home sounds.",
    icon: "CloudFog",
    accent: "violet",
    benefits: [
      { icon: "Bell", title: "Interconnected", body: "Fire in the kitchen wakes the bedrooms. As it should." },
      { icon: "BatteryFull", title: "10-year battery", body: "Sealed lithium — no nightly chirp at 3am ever again." },
    ],
    useCases: [{ title: "Two-storey home", body: "Smoke in the kitchen wakes the bedroom upstairs — every alarm, simultaneously." }],
    faqs: [{ q: "Standard?", a: "UL 217 and EN 14604 certified." }],
    keywords: "smoke detector India, fire alarm, GenieX",
  },
  "water-leak-detection": {
    title: "Water Leak Detection",
    eyebrow: "Before a drop becomes a disaster",
    tagline: "The smallest leak — caught before the largest bill.",
    intro:
      "Wireless leak sensors under sinks, behind washing machines and beside water heaters — paired with auto-shutoff valves on your mains.",
    icon: "Droplets",
    accent: "cyan",
    benefits: [
      { icon: "Zap", title: "Auto-shutoff", body: "Shuts the main water valve the moment a sensor goes wet." },
      { icon: "Bell", title: "Phone alert", body: "Know the moment your washing machine hose fails — even from another country." },
    ],
    useCases: [{ title: "Holiday home", body: "Detects a burst pipe while you are on vacation. Shuts off automatically. Saves the floor." }],
    faqs: [{ q: "Sensor battery life?", a: "5 years on a single CR123A." }],
    keywords: "water leak sensor India, smart shutoff, GenieX",
  },
  "panic-buttons": {
    title: "Panic Buttons",
    eyebrow: "One press · Immediate help",
    tagline: "When seconds matter.",
    intro:
      "Discreet panic buttons at bedside, kitchen and entrance — long-press triggers a multi-channel alert to family, security agency and the home's audio system.",
    icon: "Siren",
    accent: "amber",
    benefits: [
      { icon: "Phone", title: "Alerts everyone", body: "Family, security and (optionally) police — all at once." },
      { icon: "Volume2", title: "Audio siren", body: "Whole-home siren broadcasts via the audio system." },
    ],
    useCases: [{ title: "Elderly parents", body: "A single button by the bedside — one press alerts the entire family." }],
    faqs: [{ q: "False trigger protection?", a: "Long-press (2s) required to arm — accidental presses do nothing." }],
    keywords: "home panic button India, elderly safety, GenieX",
  },

  // ---------- SENSORS ----------
  "motion-sensors": {
    title: "Motion Sensors",
    eyebrow: "Quietly aware. Always.",
    tagline: "Light, that arrives before you.",
    intro:
      "PIR and microwave motion sensors that turn corridors, walk-in closets, bathrooms and gardens into spaces that respond to your presence.",
    icon: "Radar",
    accent: "violet",
    benefits: [
      { icon: "Zap", title: "Sub-second response", body: "Lights are on before you finish stepping into the room." },
      { icon: "Moon", title: "Night-mode dimming", body: "5% night light at 2am. Full brightness at 8am." },
      { icon: "Battery", title: "Pet-immune", body: "Ignores small pets, triggers on humans." },
    ],
    useCases: [{ title: "Bathroom at night", body: "Soft amber path light at 5% — never a 100% glare in the middle of the night." }],
    faqs: [{ q: "Range?", a: "8m radius, 110° field of view." }],
    keywords: "motion sensor India, smart sensor, GenieX",
  },
  "presence-sensors": {
    title: "Presence Sensors",
    eyebrow: "Not just movement. Presence.",
    tagline: "Lights that know you're sitting still.",
    intro:
      "mmWave radar presence sensors that detect breathing-level movement — your home knows you are still in the room, even when you are reading silently.",
    icon: "ScanFace",
    accent: "cyan",
    benefits: [
      { icon: "Eye", title: "mmWave radar", body: "Sees presence, not just motion — even your breathing." },
      { icon: "Layers", title: "Zone control", body: "Different actions for entry, seated, sleeping zones." },
    ],
    useCases: [
      { title: "Reading nook", body: "Light stays on while you read — even if you have been still for 30 minutes." },
      { title: "Home office", body: "AC and lights only run when you are actually at the desk." },
    ],
    faqs: [{ q: "Privacy?", a: "Radar, not camera — no images recorded, ever." }],
    keywords: "presence sensor, mmWave, GenieX",
  },
  "door-window-sensors": {
    title: "Door & Window Sensors",
    eyebrow: "Open · Closed · Always known",
    tagline: "Every entry, accounted for.",
    intro:
      "Magnetic reed sensors on doors and windows — paired with automation rules and the security system.",
    icon: "DoorOpen",
    accent: "amber",
    benefits: [
      { icon: "Bell", title: "Open alerts", body: "Notification when the front door opens between 11pm and 6am." },
      { icon: "Snowflake", title: "AC efficiency", body: "AC shuts off automatically when a balcony door is opened." },
    ],
    useCases: [{ title: "Parents away", body: "Get a push notification the moment any door opens. Peace of mind, from anywhere." }],
    faqs: [{ q: "Battery life?", a: "3 years on a CR2032." }],
    keywords: "door sensor India, window sensor, GenieX",
  },
  "temperature-sensors": {
    title: "Temperature Sensors",
    eyebrow: "Climate, micro-zoned",
    tagline: "The thermostat, finally accurate.",
    intro:
      "Discreet temperature sensors per room — your AC adjusts to where you actually are, not where the thermostat sits.",
    icon: "Thermometer",
    accent: "cyan",
    benefits: [
      { icon: "Snowflake", title: "Per-room control", body: "Each room maintains its own set point." },
      { icon: "Leaf", title: "Energy savings", body: "AC runs only where it needs to — 25–30% saving on bills." },
    ],
    useCases: [{ title: "Multi-AC home", body: "Bedroom at 22°C. Living at 24°C. Each AC respecting the actual room sensor, not its built-in one." }],
    faqs: [{ q: "Accuracy?", a: "±0.3°C — laboratory-grade." }],
    keywords: "smart thermostat India, temperature sensor, GenieX",
  },
  "air-quality-sensors": {
    title: "Air Quality Sensors",
    eyebrow: "PM2.5 · CO₂ · VOC",
    tagline: "Breathe well. Knowingly.",
    intro:
      "Multi-parameter indoor air quality sensors with PM2.5, CO₂, VOC and humidity — paired with your purifiers and ventilation.",
    icon: "Wind",
    accent: "violet",
    benefits: [
      { icon: "Activity", title: "Live readings", body: "Track every breath your home takes — on a dashboard, hour by hour." },
      { icon: "Zap", title: "Auto-purifier", body: "Purifiers ramp up automatically when PM2.5 crosses 35 µg/m³." },
    ],
    useCases: [{ title: "Newborn nursery", body: "Air purifier scales up the moment AQI drops. Air, that takes care of itself." }],
    faqs: [{ q: "Calibration?", a: "Factory-calibrated, re-calibrates monthly via auto-baseline." }],
    keywords: "air quality monitor India, PM2.5 sensor, GenieX",
  },
  "light-sensors": {
    title: "Light Sensors",
    eyebrow: "Daylight-aware automation",
    tagline: "Lights that read the room — literally.",
    intro:
      "Ambient lux sensors that tell your lighting and blinds the actual brightness of each room — for automation that respects daylight.",
    icon: "Sun",
    accent: "amber",
    benefits: [
      { icon: "Sun", title: "Lux measurement", body: "Real lux, not a guess based on time of day." },
      { icon: "Zap", title: "Daylight harvest", body: "Lights dim when daylight is sufficient — automatic, beautiful, frugal." },
    ],
    useCases: [{ title: "Living room", body: "Cove lighting fades up as sunset arrives — never too bright, never too late." }],
    faqs: [{ q: "Range?", a: "0–60,000 lux." }],
    keywords: "lux sensor, daylight harvest, GenieX",
  },

  // ---------- APPLIANCES & DAILY LIVING ----------
  "ac-control": {
    title: "AC Control",
    eyebrow: "Every AC, intelligent",
    tagline: "Cool, where it matters. Off, where it doesn't.",
    intro:
      "IR-blasters and per-AC controllers for split, cassette and VRV systems — voice, app, schedule and presence-aware control.",
    icon: "Snowflake",
    accent: "cyan",
    benefits: [
      { icon: "Layers", title: "Works with every brand", body: "Daikin, Mitsubishi, Hitachi, Voltas, LG, Samsung — IR-coded for India." },
      { icon: "Leaf", title: "Presence-aware", body: "AC switches off when room is empty for 15 minutes. Always." },
    ],
    useCases: [{ title: "VRV villa", body: "Per-room set points. Per-room sensors. Per-room schedules. One app." }],
    faqs: [{ q: "Inverter ACs?", a: "Fully supported — including ambient temperature feedback for true efficiency." }],
    keywords: "smart AC control India, IR blaster, GenieX",
  },
  "tv-control": {
    title: "TV Control",
    eyebrow: "Cinema, with one button",
    tagline: "Movie. Switch. Stream. Done.",
    intro:
      "Universal IR + HDMI-CEC + IP control for every TV, soundbar, set-top box and game console in the house. One scene = the perfect cinema.",
    icon: "Tv",
    accent: "violet",
    benefits: [
      { icon: "Power", title: "One-touch on/off", body: "TV + soundbar + STB — all on, set to the right input." },
      { icon: "Sparkles", title: "Scene-integrated", body: "Movie scene also turns on the TV. Lights down, screen up." },
    ],
    useCases: [{ title: "Living TV", body: "'Hey Google, play Netflix' — TV on, Netflix open, lights down, curtains close." }],
    faqs: [{ q: "Works with old TVs?", a: "Yes — IR blasters cover everything older than 2005 as well." }],
    keywords: "smart TV remote, universal remote India, GenieX",
  },
  "robot-vacuum": {
    title: "Robot Vacuum",
    eyebrow: "Clean · Mop · Mapped",
    tagline: "A home that cleans itself, while you're out.",
    intro:
      "LiDAR-mapped robot vacuums with simultaneous mop — integrated into your smart home so they only run when nobody is home.",
    icon: "BotMessageSquare",
    accent: "amber",
    benefits: [
      { icon: "Map", title: "LiDAR mapping", body: "Maps the entire home — works rooms in the order you specify." },
      { icon: "Droplets", title: "Mop + vacuum", body: "Wet-mopping and dry-vacuum in a single pass." },
      { icon: "User", title: "Presence-aware", body: "Cleans only when you are out — set in seconds." },
    ],
    useCases: [{ title: "Daily reset", body: "11am everyday, when nobody is home, your floors are spotless by 1pm." }],
    faqs: [{ q: "Carpet detection?", a: "Yes — lifts mop pad automatically when transitioning to carpet." }],
    keywords: "robot vacuum India, smart home cleaner, GenieX",
  },
  "smart-geyser": {
    title: "Smart Geyser",
    eyebrow: "Hot water · Just in time",
    tagline: "Hot water, on schedule.",
    intro:
      "Smart geyser controllers that learn your bath routine, heat just before you need it, and never run an instant longer.",
    icon: "Bath",
    accent: "violet",
    benefits: [
      { icon: "Clock", title: "Bath-time schedules", body: "Wakes the geyser 15 minutes before your bath. Off 5 minutes after." },
      { icon: "Leaf", title: "30% energy savings", body: "Never run an instant longer than needed." },
    ],
    useCases: [{ title: "Working couple", body: "Bath at 7:30am. Geyser on at 7:15am. Off at 7:45am. No buttons pressed." }],
    faqs: [{ q: "Works with old geysers?", a: "Yes — adds intelligence to any existing 2000-3500W geyser." }],
    keywords: "smart geyser, water heater automation, GenieX",
  },
  "smart-plugs": {
    title: "Smart Plugs",
    eyebrow: "Any appliance, suddenly smart",
    tagline: "Plug it in. Smart it up.",
    intro:
      "16A WiFi smart plugs with energy monitoring — turn any appliance into a smart appliance, with full energy stats on your app.",
    icon: "Plug",
    accent: "cyan",
    benefits: [
      { icon: "Activity", title: "Energy monitoring", body: "See exactly how many ₹/kWh each appliance is costing you." },
      { icon: "Power", title: "16A heavy duty", body: "Suitable for ACs, geysers, induction cooktops." },
    ],
    useCases: [{ title: "Holiday mode", body: "Turn off the 12 appliances you forgot, from the airport." }],
    faqs: [{ q: "Schedules?", a: "Yes — including astro-schedules tied to sunrise/sunset." }],
    keywords: "smart plug India, WiFi socket, GenieX",
  },
  "pet-feeders": {
    title: "Pet Feeders",
    eyebrow: "Schedule · Camera · Voice",
    tagline: "Be with your pet, even when you're not.",
    intro:
      "WiFi pet feeders with HD camera, two-way audio and scheduled portions — for the dogs and cats that don't enjoy delayed dinner.",
    icon: "Bone",
    accent: "amber",
    benefits: [
      { icon: "Clock", title: "4 meals/day", body: "Schedule portion sizes per meal." },
      { icon: "Video", title: "HD camera", body: "Watch and talk to your pet from anywhere." },
    ],
    useCases: [{ title: "Travelling owner", body: "Vacation without worrying about meal times — they get fed on the dot." }],
    faqs: [{ q: "Battery backup?", a: "Yes — 6 hours backup for power cuts." }],
    keywords: "smart pet feeder India, automatic pet feeder, GenieX",
  },

  // ---------- OUTDOOR & UTILITY ----------
  "gate-automation": {
    title: "Gate Automation",
    eyebrow: "Arrive without slowing down",
    tagline: "Arrive. The gate already knows.",
    intro:
      "Sliding and swing gate motors with vehicle recognition, smartphone unlock and intercom integration — for the moment of arrival, perfected.",
    icon: "Fence",
    accent: "amber",
    benefits: [
      { icon: "Car", title: "ANPR plate recognition", body: "Recognises your car's number plate — opens before you stop." },
      { icon: "Smartphone", title: "Geo-fenced unlock", body: "Gate opens when your phone enters the geo-fence." },
      { icon: "BatteryFull", title: "Solar option", body: "Off-grid gate for farmhouses and resorts." },
    ],
    useCases: [{ title: "Villa entry", body: "Drive in without slowing — gate already opening. Closes 30 seconds after you pass." }],
    faqs: [{ q: "Power-cut backup?", a: "Yes — battery + manual override key." }],
    keywords: "gate automation India, sliding gate motor, GenieX",
  },
  "boom-barriers": {
    title: "Boom Barriers",
    eyebrow: "Controlled access · Effortless flow",
    tagline: "Controlled access. Effortless flow.",
    intro:
      "High-speed, high-cycle boom barriers for villas, apartment complexes and commercial entrances — integrated with ANPR, RFID and intercom.",
    icon: "AlignVerticalSpaceAround",
    accent: "violet",
    benefits: [
      { icon: "Timer", title: "1.5-second cycle", body: "Lifts in 1.5 seconds — no queue ever builds up." },
      { icon: "ShieldCheck", title: "Anti-crash", body: "Safety beam reverses the boom if anything is detected underneath." },
    ],
    useCases: [{ title: "Apartment entry", body: "Resident vehicles pass without slowing. Guest vehicles trigger the intercom." }],
    faqs: [{ q: "Cycle rating?", a: "Up to 5 million cycles — designed for commercial duty." }],
    keywords: "boom barrier India, parking automation, GenieX",
  },
  "sprinkler-automation": {
    title: "Sprinkler Automation",
    eyebrow: "Lawn · Garden · Auto",
    tagline: "A garden that waters itself.",
    intro:
      "Smart irrigation controllers with weather-aware skipping, soil moisture sensors and zone-by-zone scheduling.",
    icon: "Sprout",
    accent: "cyan",
    benefits: [
      { icon: "Cloud", title: "Weather-aware", body: "Skips watering if it rained or will rain — saves 30% water." },
      { icon: "Map", title: "Zone scheduling", body: "Lawn, beds, hedges — different schedules, different rates." },
    ],
    useCases: [{ title: "Villa lawn", body: "8-zone garden waters at 5am — different durations for grass, beds and trees." }],
    faqs: [{ q: "How many zones?", a: "Up to 16 zones per controller." }],
    keywords: "smart sprinkler India, garden automation, GenieX",
  },
  "pool-control": {
    title: "Pool Control",
    eyebrow: "Heater · Filter · Lighting · Cover",
    tagline: "The pool, on its best behaviour.",
    intro:
      "Integrated control of pool heater, filter, lighting, water features and motorised covers — accessible from your bedside.",
    icon: "Waves",
    accent: "amber",
    benefits: [
      { icon: "Thermometer", title: "Heater scheduling", body: "Warm the pool 1 hour before you swim — never wasted." },
      { icon: "Sparkles", title: "RGB pool lighting", body: "Cycle colours, sync to music, or set a single elegant tone." },
    ],
    useCases: [{ title: "Weekend swim", body: "Saturday 5pm — heater on at 4pm, lights to 'sunset', cover open. All scheduled Friday." }],
    faqs: [{ q: "Salt-water pools?", a: "Yes — chlorinator control included." }],
    keywords: "smart pool control India, GenieX",
  },
  "driveway-sensors": {
    title: "Driveway Sensors",
    eyebrow: "Arrival, announced",
    tagline: "Know when someone arrives — before they ring.",
    intro:
      "Buried inductive loops and PIR beams that detect vehicles and people on the driveway — paired with audio, lighting and CCTV.",
    icon: "Radar",
    accent: "violet",
    benefits: [
      { icon: "Bell", title: "Welcome chime", body: "A discreet chime indoors when a car arrives." },
      { icon: "Sun", title: "Welcome lighting", body: "Driveway lights come up as you arrive — never a dark driveway again." },
    ],
    useCases: [{ title: "Long villa driveway", body: "Lights come on the moment a car crosses the gate — by the time you park, the porch is glowing." }],
    faqs: [{ q: "Range?", a: "Up to 12m PIR / 30m inductive." }],
    keywords: "driveway sensor, smart driveway, GenieX",
  },
};

// -----------------------------------------------------------------------------
// SOLUTION PAGES — 11 solutions.
// -----------------------------------------------------------------------------

export const SOLUTION_PAGES = {
  "complete-home": {
    title: "Complete Home Automation",
    eyebrow: "The whole story",
    tagline: "Every room. Every system. One home.",
    intro:
      "An end-to-end intelligent living solution — light, climate, security, entertainment, access and ambience — designed, installed and supported by GenieX as a single, considered experience.",
    icon: "Home",
    accent: "amber",
    benefits: [
      { icon: "Layers", title: "Every system, one app", body: "Lighting, AC, curtains, music, security, locks, sensors — orchestrated together." },
      { icon: "PenTool", title: "Architect-led design", body: "We work with your interior designer from day one — never an afterthought." },
      { icon: "ShieldCheck", title: "5-year warranty", body: "On every component. On installation. In writing." },
    ],
    useCases: [
      { title: "New 4BHK build", body: "Smart-wired during construction. Move-in day = a fully intelligent home." },
      { title: "Renovation", body: "Retrofitted floor-by-floor with zero damage — your family stays in the house through it." },
    ],
    faqs: [
      { q: "Investment range?", a: "₹6 lakh to ₹50 lakh+ depending on home size, philosophy and product mix." },
      { q: "How long does it take?", a: "Apartments: 5–10 days. Villas: 30–60 days." },
    ],
    keywords: "complete home automation India, smart villa, GenieX",
  },
  "home-theatre": {
    title: "Home Theatre Automation",
    eyebrow: "Cinema · Sound · Stage",
    tagline: "Lights down. Curtain up. One touch.",
    intro:
      "Cinematic home theatre integration — Atmos audio, 4K projection, motorised screens and rising masking, acoustic treatment and luxury seating, all on a single beautifully crafted scene.",
    icon: "Film",
    accent: "violet",
    benefits: [
      { icon: "Volume2", title: "Dolby Atmos calibrated", body: "Room-corrected acoustics — by certified THX engineers." },
      { icon: "Maximize2", title: "Motorised screens & masking", body: "16:9 to 2.35:1 cinemascope — automatic per movie." },
      { icon: "Sparkles", title: "One-button cinema", body: "Lights, blackout curtains, projector, screen, audio, AC — all in a single scene." },
    ],
    useCases: [
      { title: "Dedicated theatre", body: "From projector lift to riser-seating tactile transducers — fully integrated." },
      { title: "Living room cinema", body: "Hidden 100-inch screen lowers from the ceiling. Soundbar performs Atmos. Magic." },
    ],
    faqs: [{ q: "Budget?", a: "From ₹8 lakh (living room cinema) to ₹80 lakh (dedicated theatre suite)." }],
    keywords: "home theatre automation India, Dolby Atmos, GenieX",
  },
  "smart-living": {
    title: "Smart Living",
    eyebrow: "Intelligence, woven into life",
    tagline: "A home that responds before you ask.",
    intro:
      "The GenieX philosophy of smart living — automation that anticipates, scenes that orchestrate, and a home that quietly takes care of itself.",
    icon: "Sparkles",
    accent: "amber",
    benefits: [
      { icon: "Brain", title: "Predictive routines", body: "Learns your rhythm — and tunes the home to match." },
      { icon: "Sun", title: "Circadian lighting", body: "Light that follows the sun — better sleep, brighter days." },
      { icon: "Mic", title: "Multi-modal control", body: "Voice. App. Touch. Presence. The right one, for every moment." },
    ],
    useCases: [{ title: "Morning routine", body: "Curtains rise at 6:55am. Bedroom lights warm to 30%. AC eases off. Coffee maker starts. Your home, on your side." }],
    faqs: [{ q: "Do I need to learn anything?", a: "Nothing. Smart living should feel like magic, not homework." }],
    keywords: "smart living India, intelligent home, GenieX",
  },
  "home-security": {
    title: "Home Security",
    eyebrow: "Locked · Watched · Calm",
    tagline: "Protection that disappears into the architecture.",
    intro:
      "End-to-end home security — smart locks, video doorbells, CCTV, motion sensors, gas/water/smoke detection — orchestrated into a single peace of mind.",
    icon: "ShieldCheck",
    accent: "cyan",
    benefits: [
      { icon: "Lock", title: "Layered security", body: "Gate → Door → Hallway → Bedroom. Every layer, intelligently armed." },
      { icon: "Eye", title: "Single-app view", body: "Every camera, every lock, every sensor — one elegant app." },
      { icon: "Bell", title: "Smart alerts", body: "AI filters out wind, pets and shadows — you only get the alerts that matter." },
    ],
    useCases: [{ title: "Travelling family", body: "'Away mode' arms the home — random lights, simulated presence, all sensors live." }],
    faqs: [{ q: "Insurance benefits?", a: "Most insurers offer 10–15% premium reduction with a verified home security system." }],
    keywords: "home security India, smart security, GenieX",
  },
  "energy-saving": {
    title: "Energy Saving",
    eyebrow: "Frugality, automated",
    tagline: "Save 30%. Without ever thinking about it.",
    intro:
      "Automation that quietly switches off the lights you forgot, the AC running for empty rooms, and the geyser that should not be on at 11am.",
    icon: "Leaf",
    accent: "violet",
    benefits: [
      { icon: "Zap", title: "30% bill reduction", body: "Average across our installed homes — verified, not marketed." },
      { icon: "Activity", title: "Energy dashboard", body: "See exactly where every kWh is going — appliance by appliance." },
      { icon: "Sun", title: "Solar integration", body: "Pair with rooftop solar — schedules align with peak generation." },
    ],
    useCases: [{ title: "AC-heavy home", body: "Per-room presence-aware ACs alone save ₹4,000–7,000/month in Hyderabad summers." }],
    faqs: [{ q: "Payback period?", a: "Energy savings typically pay back the automation cost within 3–4 years." }],
    keywords: "smart home energy saving, energy efficiency India, GenieX",
  },
  "elderly-friendly": {
    title: "Elderly-Friendly Smart Home",
    eyebrow: "Independence · Safety · Dignity",
    tagline: "Independence, with quiet protection.",
    intro:
      "Smart home design specifically for elderly parents — voice-first control, fall detection, panic buttons, automated lighting, and one-button family contact.",
    icon: "Heart",
    accent: "amber",
    benefits: [
      { icon: "Mic", title: "Voice-first", body: "No app needed — 'Alexa, lights' is enough." },
      { icon: "Activity", title: "Fall detection", body: "Optional mmWave fall detection in bathrooms — with auto-alert to family." },
      { icon: "Sun", title: "Night-path lighting", body: "Soft amber path lights at 2am — prevents falls during night-time bathroom visits." },
    ],
    useCases: [{ title: "Living separately", body: "Parents in Hyderabad, you in Bangalore. Get a daily 'all well' summary, with one-tap call." }],
    faqs: [{ q: "Privacy?", a: "Radar-based detection, never camera. Only events are notified, never video." }],
    keywords: "elderly smart home India, senior care automation, GenieX",
  },
  "child-safe": {
    title: "Child-Safe Smart Home",
    eyebrow: "Watched · Limited · Loved",
    tagline: "A home that takes care of your children with you.",
    intro:
      "Childproofing for the smart age — locked switches in nurseries, blocked plugs, screen time controls, and door alerts for the moments that matter.",
    icon: "Baby",
    accent: "cyan",
    benefits: [
      { icon: "Lock", title: "Child-locked switches", body: "Touch panels can be locked from the master room — no late-night chaos." },
      { icon: "Bell", title: "Door open alerts", body: "Notified the moment a child opens the front door or balcony door." },
      { icon: "Clock", title: "Screen time", body: "TV automatically turns off at 9pm on school nights." },
    ],
    useCases: [{ title: "Toddler safety", body: "Balcony door alert + AC always-on + soft path lighting + locked kitchen plugs." }],
    faqs: [{ q: "Override?", a: "Parents can always override from the master panel or app." }],
    keywords: "child safe home automation, child proof smart home, GenieX",
  },
  "rental": {
    title: "Rental Smart Home",
    eyebrow: "Take it with you",
    tagline: "A smart home you can take with you.",
    intro:
      "Retro modules, smart plugs, wireless remotes and smart bulbs — a complete smart home that needs no civil work and detaches in 30 minutes.",
    icon: "Boxes",
    accent: "violet",
    benefits: [
      { icon: "HousePlug", title: "Zero damage", body: "No drilling, no rewiring, no broken walls." },
      { icon: "Briefcase", title: "Pack-and-go", body: "Disassembles in 30 minutes. Re-installs in your next home." },
      { icon: "Wifi", title: "WiFi-first", body: "Works with the WiFi you already have." },
    ],
    useCases: [{ title: "IT professional", body: "Smart home in Hyderabad. Move to Bangalore in 2 years — your smart home moves with you." }],
    faqs: [{ q: "Landlord permission?", a: "Generally not needed — nothing is permanent." }],
    keywords: "rental smart home India, no rewiring smart home, GenieX",
  },
  "hotel": {
    title: "Hotel Automation",
    eyebrow: "Hospitality-grade",
    tagline: "Guest experience, perfected.",
    intro:
      "Boutique-hotel-grade room automation — welcome scenes, do-not-disturb, makeup-room indicators, integrated locks and BMS — engineered for 4–5 star Indian hospitality.",
    icon: "Building2",
    accent: "amber",
    benefits: [
      { icon: "DoorOpen", title: "RFID check-in", body: "Keycard integrated with lock, lights, AC and welcome scene." },
      { icon: "Sparkles", title: "Welcome scene", body: "Curtains open, lights up, AC pre-cooled — the moment guest enters." },
      { icon: "Activity", title: "BMS integration", body: "Energy management at scale — central monitoring per floor." },
    ],
    useCases: [{ title: "Boutique resort", body: "30-room property fully integrated — staff trained in 2 days." }],
    faqs: [{ q: "PMS integration?", a: "We integrate with all major Indian PMS — IDS, Hotelogix, Opera." }],
    keywords: "hotel automation India, room automation, GenieX",
  },
  "villas": {
    title: "Villa Automation",
    eyebrow: "Estate-grade intelligence",
    tagline: "The estate, intelligent end-to-end.",
    intro:
      "Villa-scale smart home — gate to garage to garden, KNX-grade reliability, multi-zone AV, and a single app for an entire estate.",
    icon: "Castle",
    accent: "amber",
    benefits: [
      { icon: "Layers", title: "KNX backbone", body: "International standard — the wiring of palaces and Bvlgari hotels." },
      { icon: "Mic", title: "Multi-zone audio", body: "12+ zones of audio — different music in every room." },
      { icon: "ShieldCheck", title: "Perimeter security", body: "Boundary cameras + driveway sensors + gate + locks." },
    ],
    useCases: [{ title: "Hyderabad villa", body: "4000 sqft, 5BHK, KNX-wired, full security, multi-zone audio, garden automation." }],
    faqs: [{ q: "Investment?", a: "₹15 lakh – ₹1 crore+ depending on philosophy and scale." }],
    keywords: "villa automation India, luxury smart home, GenieX",
  },
  "apartments": {
    title: "Apartment Automation",
    eyebrow: "WiFi-first · Renter-friendly",
    tagline: "Luxury intelligence, apartment-sized.",
    intro:
      "WiFi-native smart home solutions tuned for 2BHK, 3BHK and 4BHK apartments — sleek, affordable, fully featured.",
    icon: "Building",
    accent: "violet",
    benefits: [
      { icon: "Wifi", title: "WiFi-first", body: "No new wiring needed — works on the WiFi you already have." },
      { icon: "Boxes", title: "Modular", body: "Start with the living room. Add bedrooms when you're ready." },
      { icon: "Smartphone", title: "All-in-one app", body: "Lights, AC, curtains, lock, doorbell — one clean app." },
    ],
    useCases: [{ title: "3BHK apartment", body: "Smart switches in every room, smart lock, doorbell, AC control — installed in 2 days, from ₹2.5 lakh." }],
    faqs: [{ q: "Society approval?", a: "Required for external doorbells and CCTV — we handle the paperwork." }],
    keywords: "apartment automation India, smart 3BHK, GenieX",
  },
  "for-architects": {
    title: "GenieX for Architects",
    eyebrow: "Specify with confidence",
    tagline: "Architects who specify GenieX never go back.",
    intro:
      "Pre-engineered specification packs, BIM-ready details, mock-up panels, and a dedicated architect liaison — we exist to make your project beautiful.",
    icon: "Compass",
    accent: "amber",
    benefits: [
      { icon: "FileText", title: "Spec sheets ready", body: "BIM, AutoCAD, Revit — every product detailed for your drawings." },
      { icon: "Layers", title: "Mock-up panels", body: "We bring physical mock-ups to your client meetings." },
      { icon: "PenTool", title: "Custom finishes", body: "We will match any architectural finish — including custom milled aluminium." },
    ],
    useCases: [{ title: "Luxury villa project", body: "30+ leading architects across South India trust GenieX for their luxury projects." }],
    faqs: [{ q: "Commissioning support?", a: "Yes — full pre-handover testing and dry runs." }],
    keywords: "smart home for architects, GenieX architect program",
  },
  "for-interior-designers": {
    title: "GenieX for Interior Designers",
    eyebrow: "Beautiful by default",
    tagline: "Designed to disappear into your vision.",
    intro:
      "Switches that match your palette, scenes that match your lighting story, and finishes that match your fabric mood board.",
    icon: "Palette",
    accent: "violet",
    benefits: [
      { icon: "Palette", title: "30+ finishes", body: "Black, white, gold, brass, copper, graphite, brushed steel and counting." },
      { icon: "Hand", title: "On-site colour matching", body: "We will bring fabric and paint samples to match your switches." },
      { icon: "Layers", title: "Lighting scene design", body: "Our team programs scenes with your lighting designer." },
    ],
    useCases: [{ title: "Designer-led project", body: "A boutique villa where every switch matches the wallpaper, and every scene matches the mood." }],
    faqs: [{ q: "Trade pricing?", a: "Yes — for verified design studios. Contact us." }],
    keywords: "smart home for interior designers, GenieX",
  },
  "for-builders": {
    title: "GenieX for Builders",
    eyebrow: "Smart-ready as a category-killer",
    tagline: "Sell more. Sell faster. Sell premium.",
    intro:
      "Builder-grade smart home packages bundled into your residential projects — every flat ships smart-ready, with a clear upsell path for the buyer.",
    icon: "Building2",
    accent: "cyan",
    benefits: [
      { icon: "TrendingUp", title: "Higher sale velocity", body: "Smart-ready listings sell 23% faster on average (industry data)." },
      { icon: "DollarSign", title: "Premium pricing", body: "₹150–400 / sqft premium achievable for verified smart-ready projects." },
      { icon: "ShieldCheck", title: "Per-flat warranty", body: "Each buyer gets a sealed warranty document on possession." },
    ],
    useCases: [{ title: "200-flat tower", body: "Pre-wired during construction, activated at handover — every flat ships smart-ready." }],
    faqs: [{ q: "MOQ?", a: "From 20 flats — pricing improves with scale." }],
    keywords: "smart home for builders India, GenieX builder program",
  },
  "for-homeowners": {
    title: "GenieX for Homeowners",
    eyebrow: "Your home, your way",
    tagline: "Start small. Live intelligently.",
    intro:
      "A guided journey for homeowners — from a single smart switch to a fully orchestrated estate, at your own pace, on your own terms.",
    icon: "Home",
    accent: "amber",
    benefits: [
      { icon: "Heart", title: "No tech talk", body: "We speak in mood, life and ease — never in protocols." },
      { icon: "Layers", title: "Start small, grow big", body: "Begin with one room. Your investment compounds." },
      { icon: "ShieldCheck", title: "5-year warranty", body: "Lifetime support, written into the contract." },
    ],
    useCases: [{ title: "First smart home", body: "Start with the master bedroom — lights, curtains, AC. ₹50,000 to magic." }],
    faqs: [{ q: "First step?", a: "A free home survey — we'll walk through your spaces and recommend a thoughtful first phase." }],
    keywords: "smart home for homeowners India, GenieX",
  },
};

// -----------------------------------------------------------------------------
// LOCATION PAGES — 15 cities across Telangana & Andhra Pradesh.
// -----------------------------------------------------------------------------

export const LOCATION_PAGES = {
  hyderabad: {
    title: "Smart Home Automation in Hyderabad",
    eyebrow: "Hyderabad · Telangana",
    tagline: "Hyderabad's most discerning homes choose GenieX.",
    intro:
      "From Jubilee Hills bungalows to Gachibowli high-rises, GenieX is the trusted smart home partner for Hyderabad's most thoughtfully designed homes. Free site survey, 5-year warranty and same-week service across the city.",
    areas: [
      "Jubilee Hills", "Banjara Hills", "Gachibowli", "Kondapur", "Madhapur",
      "Hitech City", "Kokapet", "Financial District", "Tellapur", "Manikonda",
      "Kukatpally", "Miyapur", "Begumpet", "Secunderabad", "Kompally",
      "Shamshabad", "ORR Villas", "Nanakramguda",
    ],
    services: [
      "Complete villa automation",
      "Apartment smart home (2BHK, 3BHK, 4BHK)",
      "Home theatre design and installation",
      "Smart security and CCTV",
      "Curtain & blind automation",
      "KNX wired automation for villas",
      "Builder pre-wiring projects",
      "Annual maintenance contracts",
    ],
    caseStudies: [
      { title: "Jubilee Hills Villa", body: "5BHK · KNX-wired · multi-zone audio · ANPR gate · 8-camera perimeter · ₹38 L." },
      { title: "Gachibowli 4BHK", body: "WiFi smart home · Dynamic Series · automated curtains · home theatre · ₹9 L." },
      { title: "Banjara Hills Apartment", body: "3BHK Retro Module retrofit · zero civil · installed in 2 days · ₹3 L." },
    ],
    faqs: [
      { q: "Do you do free site surveys in Hyderabad?", a: "Yes — across all areas within ORR, complimentary. Beyond ORR, ₹2,500 (adjustable against project)." },
      { q: "Typical installation time?", a: "Apartments: 5–10 days. Villas: 30–60 days." },
      { q: "After-sales service in Hyderabad?", a: "Same-week visits city-wide, with a dedicated Hyderabad service team." },
    ],
    keywords: "smart home Hyderabad, home automation Hyderabad, GenieX Hyderabad",
  },
  vijayawada: {
    title: "Smart Home Automation in Vijayawada",
    eyebrow: "Vijayawada · Andhra Pradesh",
    tagline: "Vijayawada's intelligent homes, beautifully delivered.",
    intro:
      "GenieX brings premium smart home automation to Vijayawada — from luxury villas in Tadepalli to thoughtful apartments in Benz Circle.",
    areas: [
      "Tadepalli", "Benz Circle", "MG Road", "Auto Nagar",
      "Bhavanipuram", "Currency Nagar", "Patamata", "Penamaluru",
      "Gunadala", "Kanuru", "Ramavarappadu",
    ],
    services: [
      "Villa and farmhouse automation",
      "Apartment smart home solutions",
      "Smart security and CCTV systems",
      "Home theatre and entertainment",
      "Curtain and blind motorisation",
      "Builder pre-wiring projects",
    ],
    caseStudies: [
      { title: "Tadepalli Farmhouse", body: "Sprawling 6000 sqft · KNX backbone · pool control · multi-zone audio · ₹28 L." },
      { title: "Benz Circle 3BHK", body: "Smart Touch panels · home theatre · curtain automation · ₹6.5 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Vijayawada + Tadepalli + Guntur + Tenali corridor — same-week service." },
      { q: "Site survey?", a: "Free within Vijayawada limits. Project teams travel for outstation projects." },
    ],
    keywords: "smart home Vijayawada, home automation Vijayawada, GenieX Vijayawada",
  },
  visakhapatnam: {
    title: "Smart Home Automation in Visakhapatnam",
    eyebrow: "Visakhapatnam · Andhra Pradesh",
    tagline: "Vizag's coastal homes, intelligently engineered.",
    intro:
      "Coastal homes deserve special engineering — corrosion-resistant hardware, monsoon-aware automation and premium smart home solutions tuned for Visakhapatnam's climate.",
    areas: [
      "MVP Colony", "Beach Road", "Daba Gardens", "Madhurawada",
      "Sagar Nagar", "Rushikonda", "Kommadi", "Endada",
      "Pendurthi", "PM Palem",
    ],
    services: [
      "Coastal villa automation (marine-grade)",
      "Apartment smart home solutions",
      "Home theatre and entertainment",
      "Smart security and CCTV",
      "Builder pre-wiring projects",
    ],
    caseStudies: [
      { title: "Rushikonda Sea-Facing Villa", body: "Marine-grade hardware · IP66 outdoor · KNX-wired · ₹32 L." },
      { title: "Madhurawada 4BHK", body: "WiFi smart home · home theatre · curtain automation · ₹8 L." },
    ],
    faqs: [
      { q: "Coastal corrosion?", a: "We use marine-grade hardware (316L stainless, IP66) for all outdoor installations near the sea." },
      { q: "Service coverage?", a: "Vizag city + Bheemili + Anakapalli corridor." },
    ],
    keywords: "smart home Visakhapatnam, home automation Vizag, GenieX Vizag",
  },
  guntur: {
    title: "Smart Home Automation in Guntur",
    eyebrow: "Guntur · Andhra Pradesh",
    tagline: "Guntur's premium homes, intelligently designed.",
    intro:
      "GenieX brings world-class smart home automation to Guntur's most thoughtful homes — from Brodipet to Chandramouli Puram.",
    areas: [
      "Brodipet", "Pattabhipuram", "Chandramouli Puram",
      "Lakshmipuram", "Arundelpet", "Sangadigunta",
      "Nallapadu", "Amaravathi Road",
    ],
    services: [
      "Complete villa automation",
      "Apartment smart home (2BHK, 3BHK, 4BHK)",
      "Home theatre design",
      "Smart security and CCTV",
      "Builder pre-wiring",
    ],
    caseStudies: [
      { title: "Brodipet Premium Villa", body: "5BHK · full smart home · home theatre · 12-camera security · ₹22 L." },
      { title: "Chandramouli Puram 3BHK", body: "Retro Module retrofit · smart locks · CCTV · ₹3.5 L." },
    ],
    faqs: [
      { q: "Site survey in Guntur?", a: "Free within city limits — book online or call us." },
    ],
    keywords: "smart home Guntur, home automation Guntur, GenieX Guntur",
  },
  tirupati: {
    title: "Smart Home Automation in Tirupati",
    eyebrow: "Tirupati · Andhra Pradesh",
    tagline: "Tirupati's premium residences, thoughtfully automated.",
    intro:
      "From luxury villas to thoughtful apartments, GenieX brings premium smart home automation to Tirupati and the surrounding region.",
    areas: [
      "Tiruchanoor Road", "Bairagipatteda", "Avilala", "Renigunta Road",
      "MR Palli", "Korlagunta", "AIR Bypass Road",
    ],
    services: [
      "Villa and apartment automation",
      "Home theatre and entertainment",
      "Smart security and CCTV",
      "Builder pre-wiring projects",
    ],
    caseStudies: [
      { title: "Bairagipatteda Villa", body: "4BHK · smart home · home theatre · perimeter security · ₹14 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Tirupati + Chittoor + Renigunta corridor. Project teams travel for outstation projects." },
    ],
    keywords: "smart home Tirupati, home automation Tirupati, GenieX Tirupati",
  },

  // ---------- TELANGANA (additional) ----------
  warangal: {
    title: "Smart Home Automation in Warangal",
    eyebrow: "Warangal · Telangana",
    tagline: "Warangal's heritage homes, intelligently modernised.",
    intro:
      "From Kazipet villas to Hanamkonda apartments, GenieX brings premium smart home automation to Telangana's second-largest city — with same-week service from our Hyderabad team.",
    areas: [
      "Hanamkonda", "Kazipet", "Subedari", "Hunter Road",
      "Mulugu Road", "Waddepalli", "Nakkalagutta", "Kakatiya University Road",
    ],
    services: [
      "Complete villa automation",
      "Apartment smart home (2BHK, 3BHK, 4BHK)",
      "Home theatre design",
      "Smart security and CCTV",
      "Retro Module retrofits (zero civil)",
    ],
    caseStudies: [
      { title: "Hanamkonda 4BHK", body: "WiFi smart home · Dynamic Series · home theatre · ₹7 L." },
      { title: "Kazipet Villa", body: "5BHK · KNX backbone · 8-camera security · ₹19 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Warangal city + Hanamkonda + Kazipet corridor — same-week visits from Hyderabad." },
      { q: "Free site survey?", a: "Yes, within Warangal urban limits. ₹2,500 (adjustable) for outstation." },
    ],
    keywords: "smart home Warangal, home automation Warangal, GenieX Warangal",
  },
  karimnagar: {
    title: "Smart Home Automation in Karimnagar",
    eyebrow: "Karimnagar · Telangana",
    tagline: "Karimnagar's premium homes, quietly automated.",
    intro:
      "GenieX brings world-class smart home automation to Karimnagar — from luxury independent houses to modern apartments along the LMD Colony corridor.",
    areas: [
      "LMD Colony", "Mukarampura", "Subhash Nagar", "Ramnagar",
      "Vidya Nagar", "Christianpally", "Kothirampur",
    ],
    services: [
      "Villa and independent-house automation",
      "Apartment smart home solutions",
      "Smart security and CCTV",
      "Home theatre design",
      "Retro Module retrofits",
    ],
    caseStudies: [
      { title: "LMD Colony Bungalow", body: "5BHK · full smart home · 10-camera security · ₹16 L." },
      { title: "Mukarampura 3BHK", body: "Retro Module retrofit · smart locks · CCTV · ₹3 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Karimnagar + Jagtial + Sircilla corridor, supported by our Hyderabad service team." },
    ],
    keywords: "smart home Karimnagar, home automation Karimnagar, GenieX Karimnagar",
  },
  nizamabad: {
    title: "Smart Home Automation in Nizamabad",
    eyebrow: "Nizamabad · Telangana",
    tagline: "Nizamabad's thoughtful homes, intelligently designed.",
    intro:
      "From Khaleelwadi villas to apartments around Khilla Road, GenieX brings premium smart home automation to north Telangana's largest city.",
    areas: [
      "Khaleelwadi", "Khilla Road", "Vinayak Nagar", "Subhashnagar",
      "Tilak Garden", "Vengalrao Nagar", "Bhodhan Road",
    ],
    services: [
      "Villa and independent-house automation",
      "Apartment smart home solutions",
      "Smart security and CCTV",
      "Builder pre-wiring",
    ],
    caseStudies: [
      { title: "Khaleelwadi Villa", body: "4BHK · WiFi smart home · home theatre · 8-camera security · ₹12 L." },
    ],
    faqs: [
      { q: "Site survey?", a: "Free within Nizamabad city limits. Hyderabad team travels for survey + project." },
    ],
    keywords: "smart home Nizamabad, home automation Nizamabad, GenieX Nizamabad",
  },
  khammam: {
    title: "Smart Home Automation in Khammam",
    eyebrow: "Khammam · Telangana",
    tagline: "Khammam's premium residences, beautifully connected.",
    intro:
      "GenieX brings premium smart home automation to Khammam — from independent houses along Wyra Road to apartments in Sardar Patel Nagar.",
    areas: [
      "Wyra Road", "Sardar Patel Nagar", "Khanapuram Haveli", "Polepally",
      "Rotary Nagar", "Mamillagudem", "Ballepalli",
    ],
    services: [
      "Villa and apartment automation",
      "Smart security and CCTV",
      "Home theatre design",
      "Retro Module retrofits",
    ],
    caseStudies: [
      { title: "Wyra Road Villa", body: "4BHK · smart home · curtain automation · perimeter security · ₹11 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Khammam + Kothagudem corridor. Service team travels from Hyderabad weekly." },
    ],
    keywords: "smart home Khammam, home automation Khammam, GenieX Khammam",
  },

  // ---------- ANDHRA PRADESH (additional) ----------
  nellore: {
    title: "Smart Home Automation in Nellore",
    eyebrow: "Nellore · Andhra Pradesh",
    tagline: "Nellore's premium homes, intelligently engineered.",
    intro:
      "From Dargamitta villas to apartments along Magunta Layout, GenieX brings premium smart home automation to Nellore — coastal-grade hardware for the sub-region.",
    areas: [
      "Dargamitta", "Magunta Layout", "Vedayapalem", "AC Subbareddy Nagar",
      "Stonehousepet", "Trunk Road", "Mulapet", "Brahmanandapuram",
    ],
    services: [
      "Coastal-tolerant villa automation",
      "Apartment smart home (2BHK, 3BHK, 4BHK)",
      "Home theatre design",
      "Smart security and CCTV",
      "Builder pre-wiring projects",
    ],
    caseStudies: [
      { title: "Dargamitta Villa", body: "5BHK · coastal-grade hardware · smart home · home theatre · ₹17 L." },
      { title: "Magunta Layout 3BHK", body: "WiFi smart home · curtain automation · ₹5.5 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Nellore + Gudur + Kavali corridor. Service team travels from Tirupati and Chennai." },
      { q: "Coastal corrosion?", a: "We use marine-grade outdoor hardware for any villa within 10 km of the coast." },
    ],
    keywords: "smart home Nellore, home automation Nellore, GenieX Nellore",
  },
  kakinada: {
    title: "Smart Home Automation in Kakinada",
    eyebrow: "Kakinada · Andhra Pradesh",
    tagline: "Kakinada's coastal homes, intelligently engineered.",
    intro:
      "Port-city living demands marine-grade engineering. GenieX brings premium smart home automation to Kakinada — corrosion-resistant hardware, monsoon-aware automation, white-glove service.",
    areas: [
      "Sarpavaram", "Bhanugudi Junction", "Surya Rao Peta", "Recreation Club Road",
      "Ramaraopeta", "Jagannaikpur", "Indrapalem", "Madhava Nagar",
    ],
    services: [
      "Coastal villa automation (marine-grade)",
      "Apartment smart home solutions",
      "Home theatre design",
      "Smart security and CCTV",
      "Builder pre-wiring",
    ],
    caseStudies: [
      { title: "Sarpavaram Sea-Facing Villa", body: "5BHK · marine-grade IP66 outdoor · KNX-wired · home theatre · ₹24 L." },
      { title: "Ramaraopeta 3BHK", body: "WiFi smart home · curtain automation · CCTV · ₹6 L." },
    ],
    faqs: [
      { q: "Coastal corrosion?", a: "Marine-grade 316L stainless and IP66 hardware on every outdoor element." },
      { q: "Service coverage?", a: "Kakinada + Rajahmundry + Amalapuram corridor — same-week service from Vizag." },
    ],
    keywords: "smart home Kakinada, home automation Kakinada, GenieX Kakinada",
  },
  rajahmundry: {
    title: "Smart Home Automation in Rajahmundry",
    eyebrow: "Rajahmundry · Andhra Pradesh",
    tagline: "Rajahmundry's heritage homes, intelligently reimagined.",
    intro:
      "From Morampudi villas to apartments along Danavaipeta, GenieX brings premium smart home automation to the cultural capital of Andhra — riverside-tuned and beautifully delivered.",
    areas: [
      "Morampudi", "Danavaipeta", "Lalacheruvu", "Innispeta",
      "Aryapuram", "Korukonda Road", "Diwancheruvu", "Kambala Cheruvu",
    ],
    services: [
      "Villa and apartment automation",
      "Home theatre and entertainment",
      "Smart security and CCTV",
      "Curtain and blind motorisation",
      "Builder pre-wiring projects",
    ],
    caseStudies: [
      { title: "Morampudi Villa", body: "4BHK · WiFi smart home · home theatre · perimeter security · ₹13 L." },
      { title: "Danavaipeta 3BHK", body: "Retro Module retrofit · smart locks · CCTV · ₹3.5 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Rajahmundry + Kovvur + Kakinada corridor — same-week service from Vizag." },
    ],
    keywords: "smart home Rajahmundry, home automation Rajahmundry, GenieX Rajahmundry",
  },
  kurnool: {
    title: "Smart Home Automation in Kurnool",
    eyebrow: "Kurnool · Andhra Pradesh",
    tagline: "Kurnool's thoughtful homes, beautifully automated.",
    intro:
      "GenieX brings world-class smart home automation to Kurnool — from villas along NH-44 to apartments in Bhagya Nagar and Joharapuram.",
    areas: [
      "Bhagya Nagar", "Joharapuram", "Budhwar Peta", "Krishna Nagar",
      "Kallur", "Adoni Road", "Gayatri Estates", "Bellary Road",
    ],
    services: [
      "Villa and apartment automation",
      "Smart security and CCTV",
      "Home theatre design",
      "Builder pre-wiring",
      "Retro Module retrofits",
    ],
    caseStudies: [
      { title: "Bhagya Nagar Villa", body: "4BHK · WiFi smart home · home theatre · 8-camera security · ₹12 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Kurnool + Nandyal + Adoni corridor — service team travels from Hyderabad and Anantapur." },
    ],
    keywords: "smart home Kurnool, home automation Kurnool, GenieX Kurnool",
  },
  anantapur: {
    title: "Smart Home Automation in Anantapur",
    eyebrow: "Anantapur · Andhra Pradesh",
    tagline: "Anantapur's premium homes, intelligently designed.",
    intro:
      "From villas along NH-44 to apartments in Subhash Road, GenieX brings premium smart home automation to Anantapur and the Rayalaseema region — supported from our Hyderabad and Bengaluru teams.",
    areas: [
      "Subhash Road", "Saptagiri Circle", "Kamala Nagar", "Rayalcheruvu Road",
      "Bukkarayasamudram Road", "Tapovanam", "Singanamala Road",
    ],
    services: [
      "Villa and apartment automation",
      "Home theatre design",
      "Smart security and CCTV",
      "Builder pre-wiring projects",
    ],
    caseStudies: [
      { title: "Saptagiri Circle Villa", body: "4BHK · WiFi smart home · curtain automation · CCTV · ₹10 L." },
    ],
    faqs: [
      { q: "Service coverage?", a: "Anantapur + Hindupur + Dharmavaram corridor — service team travels from Bengaluru." },
    ],
    keywords: "smart home Anantapur, home automation Anantapur, GenieX Anantapur",
  },
  amaravati: {
    title: "Smart Home Automation in Amaravati",
    eyebrow: "Amaravati · Andhra Pradesh",
    tagline: "Amaravati's new-capital homes, beautifully connected.",
    intro:
      "The greenfield capital deserves a greenfield approach. GenieX partners with builders, architects and homeowners across Amaravati's capital region to deliver pre-wired, future-ready smart homes from day one.",
    areas: [
      "Velagapudi", "Tulluru", "Mandadam", "Rayapudi",
      "Sakhamuru", "Lingayapalem", "Penumaka", "Undavalli",
    ],
    services: [
      "Builder pre-wiring projects (KNX / Zigbee)",
      "Villa and farmhouse automation",
      "Apartment smart home solutions",
      "Home theatre and entertainment",
      "Smart security and CCTV",
    ],
    caseStudies: [
      { title: "Velagapudi Builder Project", body: "120-unit KNX-ready pre-wiring across two towers · ₹4.2 Cr." },
      { title: "Undavalli Farmhouse", body: "6000 sqft · KNX backbone · pool control · multi-zone audio · ₹26 L." },
    ],
    faqs: [
      { q: "Builder pre-wiring?", a: "Yes — we're a preferred pre-wiring partner across the Amaravati capital region. KNX-ready, Zigbee-ready or both." },
      { q: "Service coverage?", a: "Amaravati + Vijayawada + Guntur corridor — same-week service." },
    ],
    keywords: "smart home Amaravati, home automation Amaravati capital region, GenieX Amaravati",
  },
};

// -----------------------------------------------------------------------------
// BLOG CONTENT — Footer-only, SEO-rich.
// -----------------------------------------------------------------------------

export const BLOG_CATEGORIES = [
  { slug: "smart-home-guides", label: "Smart Home Guides" },
  { slug: "home-security", label: "Home Security" },
  { slug: "lighting-design", label: "Lighting Design" },
  { slug: "for-architects", label: "For Architects" },
  { slug: "for-interior-designers", label: "For Interior Designers" },
  { slug: "for-homeowners", label: "For Homeowners" },
  { slug: "for-builders", label: "For Builders" },
  { slug: "location-insights", label: "Location Insights" },
  { slug: "case-studies", label: "Case Studies" },
];

// Helper to produce article bodies. Each article ships with multiple sections.
// `keywords` is a comma-separated string that we surface as the page's meta
// keywords for first-page SEO targeting.
const article = (slug, category, title, excerpt, sections, readMin = 6, keywords = "") => ({
  slug, category, title, excerpt, read: `${readMin} min`,
  date: "Feb 2026",
  sections,
  keywords,
});

export const BLOG_ARTICLES = [
  // Guides
  article("home-automation-cost-guide-india-2026", "smart-home-guides",
    "Home Automation Cost Guide for India (2026)",
    "How much should you really budget for a smart home in India? A transparent breakdown across apartments, villas and luxury homes.",
    [
      { heading: "The honest range", body: "Home automation in India in 2026 starts at ₹1.5 lakh for a thoughtful 2BHK retrofit, sits between ₹6–15 lakh for premium apartments and new villas, and crosses ₹50 lakh+ for KNX-wired luxury estates with home theatre and perimeter security." },
      { heading: "What changes the number", body: "Three variables dominate cost: protocol choice (WiFi vs KNX), product mix (smart switches vs Dynamic Series panels) and depth (per-room vs whole-home). Civil work is a fourth — retrofits save 20–30% by avoiding any rewiring." },
      { heading: "A realistic apartment", body: "A 3BHK Hyderabad apartment, fully automated with Dynamic Series, smart locks, curtain automation, AC control and CCTV typically lands at ₹6–9 lakh. With Retro Module instead of Dynamic Series, the same scope drops to ₹3–4 lakh." },
      { heading: "A realistic villa", body: "A 5BHK villa with KNX backbone, multi-zone audio, home theatre, motorised curtains, full security and gate automation typically lands at ₹25–45 lakh." },
      { heading: "How GenieX prices", body: "Transparent line-item quotations. Indian rupees. No hidden imports. 5-year warranty written into the contract." },
    ], 9),
  article("knx-vs-wifi-automation-india", "smart-home-guides",
    "KNX vs WiFi Automation — Which is right for you?",
    "The most asked question in luxury home automation. We break down reliability, flexibility, scale and cost in plain language.",
    [
      { heading: "The short answer", body: "KNX for villas you'll own for 25+ years. WiFi for apartments and homes you may move out of. Both are excellent when correctly designed." },
      { heading: "KNX, explained", body: "KNX is a wired international standard with 30+ years of reliability. The wiring is decentralised — each device on the bus communicates without a central server. It is what palaces, embassies, Bvlgari hotels and Aman resorts use." },
      { heading: "WiFi, explained", body: "Modern WiFi smart home systems run on your home network with local LAN failover. They are remarkably reliable when designed by professionals — and far more flexible because you can add devices anytime." },
      { heading: "Which one to pick", body: "Villa from scratch? KNX. Renovation? KNX or WiFi. Apartment? WiFi. Rental? WiFi (Retro Module). When in doubt, book a private consultation with us — we will recommend honestly." },
    ], 8),
  article("smart-home-ideas-for-villas", "smart-home-guides",
    "Smart Home Ideas for Villas",
    "Ten ways a villa can be quietly transformed by intelligence — without ever feeling like a tech showroom.",
    [
      { heading: "Gate automation that recognises your car", body: "ANPR cameras open the gate before you stop. Closes 30 seconds after you pass through. Welcome chime indoors lets the household know." },
      { heading: "Driveway lighting that wakes up as you arrive", body: "Path lights step up gently as your car crosses the inner gate. Porch lights at 60% until you park, then 100%. Architectural drama, every evening." },
      { heading: "Lutron-quality dimming on every chandelier", body: "Phase-cut dimmers tuned per fixture. Scenes for breakfast, lunch, dinner, party — all on the dining room panel." },
      { heading: "Curtain automation in every bedroom", body: "Whisper-silent motors that open with sunrise and close with sunset. Goodnight scene closes blackout drapes." },
      { heading: "A single app for the entire estate", body: "Gates, pool, garden, security, climate — one elegant app, with role-based access for family vs staff." },
    ], 9),
  article("smart-home-ideas-for-apartments", "smart-home-guides",
    "Smart Home Ideas for Apartments",
    "You don't need a villa to live intelligently. Here is what every premium apartment owner should consider.",
    [
      { heading: "Start with the four touch-points that matter most", body: "Entry, living, master bedroom, master bathroom. Get these four right, the rest follows naturally." },
      { heading: "The first four products", body: "Smart lock, smart lighting (Dynamic Series or Retro Module), AC control (IR blasters per AC), curtain automation in the master bedroom." },
      { heading: "Add later, at your pace", body: "Kids' room blackout blinds. CCTV at the front door. Smart geyser. Water leak sensors. Build up over 12–24 months, room by room." },
      { heading: "The retro module advantage", body: "Zero damage to your interiors, no rewiring, fully reversible — and you can take it with you when you move." },
    ], 6),
  article("benefits-of-smart-lighting", "lighting-design",
    "Why Smart Lighting is the easiest, most rewarding way to start",
    "Mood. Health. Energy savings. The case for smart lighting is overwhelming.",
    [
      { heading: "Lighting is 80% of the smart home payoff for 20% of the cost", body: "Once you live with scenes — Relax, Read, Movie, Goodnight — you cannot live without them. It is the upgrade you will tell your friends about." },
      { heading: "Circadian lighting actually helps you sleep", body: "Tunable white light that shifts from energising 5000K mornings to relaxing 2700K evenings is the closest a home can get to mimicking sunlight." },
      { heading: "Energy savings are real", body: "Presence-aware lighting + dimming + scheduling typically reduces lighting energy use by 30–40%." },
    ], 5),
  article("home-theatre-automation-guide", "smart-home-guides",
    "Home Theatre Automation Guide",
    "Build a cinema at home that turns on with one touch — and disappears when you don't need it.",
    [
      { heading: "Single-button cinema", body: "One button: lights down, curtains close, screen drops, projector on, audio set to Dolby Atmos, AC drops to 22°C. Five seconds end to end." },
      { heading: "Dedicated vs living-room theatres", body: "Dedicated theatres allow rising masking screens, riser seating, acoustic treatment — easily ₹30–80 lakh. Living-room theatres with hidden 100-inch motorised screens land at ₹8–15 lakh." },
      { heading: "What we always include", body: "Calibrated Atmos audio, motorised cinemascope masking, scene-locked AC (so it doesn't kick in mid-movie), and biometric kid lock-out for late-night thrillers." },
    ], 9),
  article("smart-locks-explained-india", "home-security",
    "Smart Locks Explained — Six ways into a modern home",
    "Face recognition, fingerprint, RFID, mobile, PIN, key — and how to pick the right combination.",
    [
      { heading: "Six ways in", body: "Modern smart locks support face recognition, fingerprint, RFID card, mobile key (Bluetooth), PIN code, and mechanical key. Pick at least three for your family." },
      { heading: "The Indian context", body: "Indian doors are typically 35–60mm thick wood or steel. Look for locks rated for both, with IP55 outdoor weatherproofing for villa front doors." },
      { heading: "Hospitality use cases", body: "Air-bnb hosts can send time-limited mobile keys via WhatsApp. Domestic help can be granted fingerprint access between 9am-1pm only." },
      { heading: "Battery and safety", body: "Modern smart locks last 6–12 months on AA batteries, with USB-C emergency power port and always a mechanical key fallback." },
    ], 6),
  article("cctv-buying-guide-india", "home-security",
    "CCTV Buying Guide for Homes",
    "Resolution, storage, night vision, cloud — what actually matters when choosing CCTV.",
    [
      { heading: "Most homes are over-cameraed and under-monitored", body: "16 cameras is rarely better than 8 well-placed ones. Cover entries, perimeter and high-value rooms — skip the rest." },
      { heading: "Resolution that's enough", body: "4MP is the sweet spot. 8MP for licence-plate reading at the gate. Anything higher mostly wastes storage." },
      { heading: "Cloud + NVR is non-negotiable", body: "Local NVR for redundancy, cloud (encrypted, India-hosted) for tamper protection. Anything less is incomplete." },
    ], 6),
  article("curtain-automation-guide", "smart-home-guides",
    "Curtain Automation Guide",
    "Whisper-quiet motors, voice control and scenes — the most delightful upgrade in a smart home.",
    [
      { heading: "Wake up to gentle sunlight", body: "Curtains rising at sunrise are nature's alarm clock. Sleep researchers prefer it to any other wake-up method." },
      { heading: "Close the bedroom with one tap", body: "Goodnight scene closes blackout curtains, dims lights to 5%, drops AC to 22°C. Done." },
      { heading: "Live like a hotel suite", body: "Once you live with curtain automation, you cannot live without it. Period." },
    ], 5),
  article("luxury-home-trends-2026", "smart-home-guides",
    "Luxury Home Technology Trends 2026",
    "Where premium homes are going next — and what we are quietly building for our clients.",
    [
      { heading: "Ambient computing", body: "Less screens, more sensors. The smart home is becoming invisible — the way it always should have been." },
      { heading: "Voice-less control", body: "Presence sensors, mmWave radar, geo-fenced unlocks — your home knows what you want without you asking." },
      { heading: "Predictive routines", body: "Light, climate and curtains learn from a week of your life — then anticipate your needs every day after." },
    ], 8),

  // Location insights
  article("smart-home-jubilee-hills-villas", "location-insights",
    "Smart Home for Jubilee Hills Villas",
    "Why Hyderabad's most prestigious neighbourhood chooses KNX-wired luxury automation.",
    [
      { heading: "The Jubilee Hills brief", body: "Villas of 5,000–15,000 sqft, often architect-led, with budget headroom and discerning taste. Smart home expectations match the architecture — invisible until summoned." },
      { heading: "What we install most", body: "KNX backbone, Dynamic Series in master suites, multi-zone audio, ANPR gates, motorised drapes throughout, home theatre, perimeter security." },
      { heading: "Budgets we see", body: "₹25 lakh to ₹1 crore+, depending on whether home theatre and pool automation are in scope." },
    ], 7),
  article("smart-home-gachibowli-apartments", "location-insights",
    "Smart Home for Gachibowli & Financial District Apartments",
    "What Hyderabad's IT corridor residents typically choose for their premium apartments.",
    [
      { heading: "The Gachibowli profile", body: "3–4 BHK premium apartments, tech-fluent owners, often dual-income, often investing first time. Strong appetite for smart home." },
      { heading: "Most popular package", body: "Dynamic Series in master and living. AC control on all 4 ACs. Smart locks. CCTV at the door. Curtain automation in master and living. ₹4–8 lakh." },
      { heading: "Retro Module alternative", body: "Renters and budget-conscious owners get a similar feature set at ₹2.5–4 lakh." },
    ], 6),
  article("smart-home-banjara-hills", "location-insights",
    "Smart Home for Banjara Hills Residences",
    "Premium automation for Hyderabad's most established luxury neighbourhood.",
    [
      { heading: "What Banjara Hills demands", body: "Heritage interiors, designer-led renovations, smart home that respects the architecture — never imposes." },
      { heading: "Most common scope", body: "Curtain automation, scene controllers, Lutron-quality dimming, smart locks, CCTV. Often integrated into an existing renovation." },
    ], 5),
  article("smart-home-kondapur-madhapur", "location-insights",
    "Smart Home for Kondapur, Madhapur and Hitech City",
    "Where the smart home conversation in Hyderabad starts today.",
    [
      { heading: "The Hitech corridor", body: "Apartments, gated communities, premium villas — the densest concentration of smart home interest in South India." },
      { heading: "What we see most", body: "WiFi-native smart home for 3BHK and 4BHK. Smart Touch panels and Dynamic Series in equal measure." },
    ], 5),
  article("smart-home-tadepalli-vijayawada", "location-insights",
    "Smart Home for Tadepalli Farmhouses (Vijayawada)",
    "Vijayawada's farmhouse belt is rapidly adopting world-class smart home automation — here's what works, and what to budget.",
    [
      { heading: "Why Tadepalli leads Vijayawada", body: "Larger plots, KNX-friendly architecture, and owners who value privacy and security — Tadepalli farmhouses are perfect candidates for end-to-end automation. Most farmhouses we automate here are 6,000–12,000 sqft, with separate guest blocks, pools and large landscaped grounds." },
      { heading: "What we typically install", body: "KNX backbone end-to-end. Dynamic Series 8M/12M panels in master and living. Multi-zone audio across the entertainment areas and the pool deck. ANPR gates. Perimeter security with 16–24 cameras. Pool automation. Motorised drapes throughout." },
      { heading: "Budgets we see", body: "₹22–55 lakh for a 6,000–10,000 sqft farmhouse. Larger estates with home theatre and pool houses cross ₹70 lakh." },
      { heading: "Build-time considerations", body: "We prefer to be on-site during the architect's SD phase — KNX conduiting must be planned with civil works, not retrofitted. 12-week installation window from base-shell to commissioning is typical." },
    ], 6,
    "Tadepalli smart home, farmhouse automation Vijayawada, KNX farmhouse India, luxury farmhouse smart home Andhra Pradesh"),
  article("smart-home-rushikonda-beach-road", "location-insights",
    "Smart Home for Rushikonda & Beach Road Vizag",
    "Coastal villa automation — marine-grade hardware, monsoon-aware engineering and 5-year warranty against salt corrosion.",
    [
      { heading: "Coastal special considerations", body: "316L stainless on all outdoor hardware. IP66 enclosures. Surge protection for the salt-air voltage swings. Standard automation kits will fail in 2 years here — ours don't, and we warrant against coastal corrosion for 5 full years." },
      { heading: "What we install most on the Vizag coast", body: "Marine-grade outdoor lighting. Storm-aware curtain retraction (cyclone alerts auto-close drapes). Sealed gate ANPR systems. Salt-tolerant outdoor speakers. Indoor Dynamic Series panels (sheltered from coastal exposure) for everything else." },
      { heading: "Budgets we see", body: "₹14–35 lakh for a 4,000–8,000 sqft sea-facing villa, depending on outdoor automation scope (pool, gate, garden lighting)." },
      { heading: "Service coverage", body: "Same-week service across Rushikonda, Beach Road, Madhurawada and the Vizag old-city corridor. Quarterly preventive maintenance is included in our 5-year warranty." },
    ], 6,
    "Vizag coastal smart home, Rushikonda villa automation, marine grade smart home India, Beach Road Vizag home automation, salt resistant smart home India"),

  // For Architects
  article("specifying-smart-home-india", "for-architects",
    "How to Specify Smart Home Automation in Your Project",
    "A practical guide for architects designing premium homes in India.",
    [
      { heading: "Specify early, not late", body: "Smart home considerations belong in the schematic design phase. Conduit pathways, panel locations, network topology — decided at SD, not at handover." },
      { heading: "What to include in the BOQ", body: "Switch count per room, panel locations, conduit sizing, network closet location, IR/RF blanket coverage, CCTV count and angles, motorised drape tracks." },
      { heading: "What GenieX provides for architects", body: "BIM blocks, Revit families, AutoCAD details, mock-up panels, and a dedicated architect liaison through the project." },
    ], 8),
  article("knx-wiring-fundamentals-for-architects", "for-architects",
    "KNX Wiring Fundamentals for Architects",
    "What every luxury-villa architect needs to know about KNX bus topology, pre-wiring and budget impact.",
    [
      { heading: "Topology", body: "KNX is a decentralised bus — a single twisted pair runs to every device. No central server, no single point of failure." },
      { heading: "Pre-wiring spec", body: "KNX bus cable (JY(St)Y 2x2x0.8) parallel to mains, separated by 50mm. Loop topology with terminators at far ends." },
      { heading: "Budget rule of thumb", body: "KNX adds ₹350–600 / sqft to a villa. Includes hardware, wiring, programming and commissioning." },
    ], 7),

  // For Interior Designers
  article("matching-switches-to-mood-board", "for-interior-designers",
    "Matching Smart Switches to Your Mood Board",
    "30+ finish options, custom milling and lighting scenes that respect your design intent.",
    [
      { heading: "30+ ready finishes", body: "Black, white, champagne gold, brushed steel, brass, copper, graphite, smoke glass, walnut, marble laminate — and we can custom-mill aluminium to almost any pantone." },
      { heading: "Lighting scenes are design too", body: "The colour temperature, dimming curves and scene transitions should be specified the way you specify upholstery — with intention." },
    ], 6),
  article("lighting-design-fundamentals", "lighting-design",
    "Lighting Design Fundamentals for Premium Homes",
    "Layering, colour temperature, scene design — the principles behind every great smart-home lighting plan.",
    [
      { heading: "The three layers", body: "Ambient (general illumination), task (reading, kitchen), accent (art, architecture). Every room should have all three, on independent circuits." },
      { heading: "Colour temperature by room", body: "Living: 3000K. Bedroom: 2700K. Kitchen task: 4000K. Bathroom: 3500K. Tunable white solves all of this if you commit to it." },
      { heading: "Scene design", body: "5–7 named scenes per room is the sweet spot. More and they stop being used. Fewer and you feel constrained." },
    ], 7),
  article("designer-led-scene-programming", "for-interior-designers",
    "Designer-Led Scene Programming",
    "How GenieX works hand-in-hand with interior designers to deliver scene programming as design — not as an afterthought.",
    [
      { heading: "Our process", body: "We program scenes on-site with the designer in the room. Adjusting until each scene matches the mood the designer had in mind during the SD phase. No remote presets. No generic palettes. Every scene tuned to the room it lives in." },
      { heading: "When we step in", body: "Ideally during the lighting layout review — 4–6 weeks before handover. We need to see the fixtures, the furniture intent, and the upholstery palette. Scenes are designed against those, not against a spec sheet." },
      { heading: "What we deliver", body: "5–7 named scenes per room, tuned for colour temperature, dimming curve and transition speed. A printed scene-card for the homeowner. A 30-minute walk-through during handover so the design intent is communicated, not just installed." },
      { heading: "The designer's brand stays on the wall", body: "We never put our brand on the switch glass. We never call out our scenes to the client. The interior designer remains the author of the experience — we are the quiet engineering team behind it." },
    ], 7,
    "designer led smart home India, scene programming interior designer, smart home for interior designers India, lighting scenes premium home"),
  article("commissioning-checklist-architects", "for-architects",
    "Smart Home Commissioning Checklist for Architects",
    "The pre-handover checklist we wish every project followed — straight from 200+ luxury commissioning runs.",
    [
      { heading: "Pre-handover (T-7 days)", body: "Every switch labelled. Every scene tested. Every camera angle reviewed. Every device named in the app. Every family member onboarded. Network closet labelled. Cable schematic delivered to architect and homeowner." },
      { heading: "Day-of-handover", body: "Walk-through with the homeowner, ideally with the interior designer present. Family-specific scene tuning. Warranty card sealed and delivered. Spare-parts inventory delivered. Emergency contact pinned on the network closet." },
      { heading: "T+30 days", body: "First scheduled check-in. App usage review. Any scenes that aren't being used — we re-tune or retire them. Any scenes the family asked for — we add them at no charge." },
      { heading: "T+90 days, T+365 days", body: "Quarterly remote health check. Annual on-site preventive maintenance for the first 5 years (included in our standard warranty). Firmware updates rolled out with notice, not silently." },
    ], 6,
    "smart home commissioning checklist, architect handover smart home, smart home project handover India, KNX commissioning India"),

  // For Homeowners
  article("how-to-start-your-smart-home", "for-homeowners",
    "How to Start Your Smart Home — A Homeowner's Guide",
    "Begin with the four products that matter most. Build at your pace.",
    [
      { heading: "Start with one room", body: "Master bedroom is the most rewarding first investment — smart lock, lighting, AC, curtain. ₹50,000 to magic." },
      { heading: "Add one room at a time", body: "Living room next. Then kids' rooms. Then kitchen. Then bathrooms. Compound your investment month by month." },
      { heading: "Resist the temptation to buy everything at once", body: "You will learn what your family actually uses after 4–6 weeks. Buy the next layer based on real life, not a sales pitch." },
    ], 5),
  article("first-smart-home-mistakes", "for-homeowners",
    "5 Mistakes Every First Smart Home Owner Makes",
    "Don't fall for the same traps we have seen for ten years.",
    [
      { heading: "Mistake 1: buying too much", body: "You don't need 200 smart bulbs. You need scenes." },
      { heading: "Mistake 2: cheap brands", body: "₹400 switches feel cheap and fail in 18 months. Spend properly." },
      { heading: "Mistake 3: ignoring the app", body: "Bad apps make the home worse, not better. Test the app for a week before you commit." },
      { heading: "Mistake 4: skipping voice", body: "Once you say 'lights off' from the bed, you cannot go back." },
      { heading: "Mistake 5: no warranty", body: "5-year warranty in writing — non-negotiable." },
    ], 6),

  // For Builders
  article("smart-home-builder-handbook", "for-builders",
    "The Builder's Handbook to Smart-Ready Projects",
    "How residential developers in India are using smart home as a category-killer differentiator.",
    [
      { heading: "Pre-wiring matters", body: "Smart-ready pre-wiring during construction is 70% cheaper than retrofit. Adds ₹50–150/sqft. Recoverable at ₹150–400/sqft premium pricing." },
      { heading: "The buyer's perception", body: "Smart-ready listings sell 23% faster on average. Builders win on velocity, not just margin." },
      { heading: "GenieX builder programme", body: "From 20 flats upwards. Pre-wiring during shell, activation at handover, per-flat warranty card, optional buyer upsell." },
    ], 7),

  // Case Studies
  article("case-jubilee-hills-knx-villa", "case-studies",
    "Case Study — Jubilee Hills KNX Villa (Hyderabad)",
    "5BHK · KNX-wired · 12-zone audio · ANPR gate · home theatre · ₹38 L.",
    [
      { heading: "The brief", body: "A renowned Hyderabad family wanted their new 5,500 sqft Jubilee Hills villa to feel like a private resort — invisible technology, immaculate ambience." },
      { heading: "What we built", body: "KNX backbone. Dynamic Series in master suite, Smart Touch elsewhere. Dolby Atmos home theatre. 12-zone multi-room audio. ANPR gate. 16-camera perimeter. Motorised drapes throughout." },
      { heading: "The result", body: "A villa where technology is felt, never seen. Handed over on time, on budget, with the architect's enthusiastic endorsement." },
    ], 6),
  article("case-gachibowli-3bhk", "case-studies",
    "Case Study — Gachibowli 3BHK Premium Apartment",
    "WiFi smart home · Dynamic Series · automated curtains · home theatre · ₹9 L.",
    [
      { heading: "The brief", body: "A young tech-couple in a premium Gachibowli high-rise wanted a complete smart home, installed in under 7 days, without civil work." },
      { heading: "What we built", body: "Dynamic Series in living and master. Smart switches in other rooms. Smart locks. Curtain automation in living and master. Home theatre with a hidden 100-inch motorised screen." },
      { heading: "The result", body: "Move-in to fully smart in 6 days. Owner reports 'cannot imagine living without it now'." },
    ], 5,
    "Gachibowli smart home case study, 3BHK home automation Hyderabad, premium apartment smart home India"),

  // ---------- 6 NEW HIGH-INTENT SEO ARTICLES (Feb 2026) ----------
  article("best-smart-switches-india-2026", "smart-home-guides",
    "10 Best Smart Switches in India (2026) — Honest Comparison",
    "We tested every premium smart switch sold in India in 2026 — price, finish, reliability, app and warranty. Here's how they actually stack up.",
    [
      { heading: "What makes a smart switch worth buying", body: "Five things matter: physical build (glass + aluminium, not plastic), the touch response curve (capacitive, not resistive), protocol (Zigbee 3.0 or KNX, not WiFi-only), the app, and the warranty. Skip any one and you'll regret it within 18 months. We've serviced over 1,200 homes — the failure patterns are remarkably consistent." },
      { heading: "Our pick: GenieX Dynamic Series", body: "Sculpted glass front, milled aluminium edge, hand-finished keys, always-on ambient display, Zigbee 3.0 mesh, hand-applied finishes across 7 colourways. Made in India, tuned for Indian voltage, 5-year written warranty. ₹6,500–₹38,000 per panel depending on module size (4M to 12M)." },
      { heading: "Imported alternatives we respect", body: "Lutron HomeWorks (overkill for most Indian homes, ₹50,000+ per gangbox), Crestron (commissioning-heavy, ₹65,000+ per panel), Schneider Wiser (solid but plastic, ₹4,000–₹8,000), Legrand Arteor (handsome metal finishes, ₹6,000–₹14,000)." },
      { heading: "Mid-tier brands to consider", body: "Anchor Roma Smart, Havells Reo — all functional WiFi-based smart switches in the ₹1,500–₹4,000 range. They will work. They will also feel like ₹2,000 switches every single day." },
      { heading: "Brands we wouldn't put in our own homes", body: "We don't name names publicly — but as a rule, avoid any switch under ₹600, any switch that uses resistive touch, any switch that only supports WiFi (with no LAN fallback) and any switch without a written multi-year warranty." },
      { heading: "How to choose the right one for you", body: "Apartment for the next 3–5 years? Mid-tier WiFi switches work fine. Apartment you plan to own for 15+ years? Dynamic Series or KNX. Villa for life? KNX backbone + Dynamic Series panels. Match the switch to how long you plan to live with it." },
      { heading: "Quick price reference", body: "Entry: ₹600–₹2,000 per module. Mid-tier: ₹2,000–₹6,000. Premium (Dynamic Series): ₹6,500–₹38,000 per panel. Imported (Lutron / Crestron): ₹50,000+ per gangbox." },
    ], 11,
    "best smart switches India 2026, top smart switches India, smart switch brands India, GenieX Dynamic Series, Lutron India, Schneider Wiser, Crestron India, best home automation switch"),

  article("smart-home-cost-3bhk-4bhk-india-2026", "smart-home-guides",
    "Smart Home Automation Cost for 3BHK & 4BHK in India (2026)",
    "Exactly what a 3BHK or 4BHK smart home costs in 2026 — itemised by package, with three real budget tiers and what you get at each.",
    [
      { heading: "The headline numbers", body: "A 3BHK premium apartment in 2026 typically lands between ₹2.5 lakh (entry retrofit) and ₹12 lakh (full premium). A 4BHK villa between ₹6 lakh (apartment-style) and ₹35 lakh (KNX-backbone, home-theatre included). Most clients sit between ₹4–9 lakh for a 3BHK and ₹12–22 lakh for a 4BHK." },
      { heading: "Tier 1 — Essential (₹2.5–4 L for 3BHK)", body: "Smart locks at entry, Retro Module on the existing switches (no rewiring), 2–3 AC IR controllers, basic curtain automation in the master, 2-camera CCTV at the door. Installs in 3 days." },
      { heading: "Tier 2 — Comfort (₹5–8 L for 3BHK)", body: "Dynamic Series panels in living + master, smart locks, full AC control, curtain automation in living + 2 bedrooms, 4-camera CCTV, smart video doorbell. Installs in 5–7 days." },
      { heading: "Tier 3 — Premium (₹9–14 L for 3BHK)", body: "Dynamic Series in every room, KNX backbone option, Atmos-ready living room with motorised projector screen, full curtain automation, smart geyser, 8-camera CCTV with cloud + NVR, biometric door, voice across all rooms. Installs in 10–14 days." },
      { heading: "What costs the most", body: "Home theatre (₹4–15 lakh), KNX backbone (₹350–600/sqft adds), motorised curtains (₹18,000–₹45,000 per window), Dynamic Series panels (₹6,500–₹38,000 each)." },
      { heading: "What costs the least (but matters most)", body: "Smart locks (₹15,000–₹45,000), curtain motors in just the master bedroom (₹18,000–₹30,000), AC IR controllers (₹4,500 each). These four touch-points deliver 70% of the smart-home experience." },
      { heading: "Hidden costs you should know about", body: "Network upgrade (₹15,000–₹40,000), electrician charges (₹500–₹2,000 per point), GST 18% on the whole quotation, annual support contract optional (₹6,000–₹18,000/year)." },
      { heading: "Why GenieX pricing is transparent", body: "Every quotation is line-item. Indian Rupees. No hidden imports. No bundled mystery items. 5-year warranty in writing. Same-week service across South India." },
    ], 12,
    "smart home cost 3BHK 4BHK India, home automation pricing India 2026, smart home budget India, smart home 3BHK Hyderabad cost, 4BHK villa automation price India"),

  article("alexa-vs-google-home-vs-apple-homekit-india", "smart-home-guides",
    "Alexa vs Google Home vs Apple HomeKit in India (2026 Comparison)",
    "Which voice ecosystem should your smart home actually plug into in India? An honest, vendor-neutral comparison from a team that installs all three.",
    [
      { heading: "The bottom line", body: "In India in 2026, Google Home is the most reliable across the widest range of devices. Alexa is the most affordable to get started. Apple HomeKit is the most private, the most beautiful — and the most limited. The right choice depends on what phones your family actually carries." },
      { heading: "Alexa — best for affordable starts", body: "Echo Dot at ₹3,999, Echo Show 8 at ₹13,999. Largest skill marketplace. Works with the broadest range of Indian-priced smart devices. Multi-room audio is excellent. Privacy controls are decent." },
      { heading: "Google Home — best for Android households", body: "Nest Hub 2 at ₹9,999, Nest Audio at ₹6,999. Best natural language understanding in 2026. Excellent Routines. Tight integration with Android phones. Works with almost every smart product sold in India." },
      { heading: "Apple HomeKit — best for iPhone households", body: "HomePod mini at ₹9,900, HomePod (2nd gen) at ₹32,900. Strictest privacy posture of the three. Beautiful Home app on iPhone. The most limited device catalogue in India, though every device that does work is rigorously vetted." },
      { heading: "What we recommend in practice", body: "Mixed Android/iPhone household? Google Home as the primary, with HomeKit on iPhones for private control. All-iPhone household with budget? HomeKit-first. Tight budget across any household? Alexa." },
      { heading: "Can I use more than one?", body: "Yes. GenieX Dynamic Series exposes every device to all three platforms simultaneously via Matter/Zigbee. We routinely set up homes where each family member uses their preferred voice assistant on their own devices." },
      { heading: "Privacy considerations for Indian homes", body: "If privacy matters most: HomeKit local processing first, Google Home second, Alexa last. All three can be muted, scheduled and audited from the wall via a single Dynamic Series panel." },
    ], 10,
    "Alexa vs Google Home vs Apple HomeKit India, best voice assistant India 2026, smart home voice control India, HomeKit India, Google Nest India, Alexa India review"),

  article("smart-home-rental-properties-india-roi", "for-homeowners",
    "Smart Home for Rental Properties — ROI Guide for Indian Landlords",
    "How much premium can a landlord charge for a smart-home-ready apartment in India? Real numbers from our 200+ rental retrofits.",
    [
      { heading: "The premium is real", body: "Across the 200+ rental units we've smart-home-fitted in Hyderabad and Bengaluru between 2022–2025, the average rent premium landlords achieved was 14–22% on the post-retrofit asking rent — with a 28% faster lease close." },
      { heading: "What renters actually pay for", body: "In order of paid willingness-to-pay: smart locks with mobile key sharing, AC remote control + scheduling, curtain automation in the master bedroom, smart geyser, smart locks at the gate (for societies). Wallpaper-led lighting scenes barely move the needle for renters." },
      { heading: "The Retro Module sweet spot", body: "₹1.2–2.8 lakh per apartment, fully reversible, zero civil work, fully recoverable (you can take the modules with you to your next property). Most Hyderabad landlords break even on the retrofit within 13–19 months from the rent premium alone." },
      { heading: "What NOT to install in rentals", body: "Skip: integrated home theatre (you'll never recover this on rent), wired KNX (locks you in), high-end Dynamic Series panels (overspec for the renter price band). Stick to retrofit-grade." },
      { heading: "The Airbnb / serviced apartment angle", body: "Smart locks with time-limited mobile keys mean you can run a serviced apartment in another city, hand keys to housekeeping and guests via WhatsApp, and never visit. Average serviced-apartment premium for fully-smart units in 2025 was 31%." },
      { heading: "Tax & accounting note", body: "Smart-home retrofits are typically depreciable as Plant & Machinery (15% WDV) for landlords filing under business income. Speak to your CA — but most clients fully expense the retrofit in 3–5 years." },
      { heading: "GenieX Rental Programme", body: "Volume-discounted Retro Module bundles for landlords with 3+ units. Centralised admin app to manage all properties. Same-week tenant support across South India. Get in touch for a portfolio audit." },
    ], 11,
    "smart home rental property India, rent premium smart home India, Airbnb smart home India, landlord smart home India, smart lock rental India, ROI smart home India"),

  article("elderly-friendly-smart-home-india", "for-homeowners",
    "Smart Home for Elderly Parents — Safety, Independence, Peace of Mind",
    "When your parents live in another city — or independently in their own home — smart automation is no longer a luxury. It is dignity, safety and presence at scale.",
    [
      { heading: "The hardest conversation, made easier", body: "Smart home for elderly Indian parents is not about gadgets. It's about three quiet promises: they will not fall in the dark, they will not be locked out, and you will know — instantly — if something is wrong. Every product we recommend is filtered through these three lenses." },
      { heading: "Motion-aware floor lighting", body: "Night-time bathroom trips are the single biggest fall risk for elderly Indians. PIR sensors at floor level trigger gentle 5% lights along the path to the bathroom. No switches needed. No glare. Just a soft glow that prevents falls. ₹12,000–₹22,000 for a typical 2BHK." },
      { heading: "One-tap emergency", body: "A single dedicated 'SOS' key on every Dynamic Series panel — and a wearable pendant for the elderly themselves — that calls 3 family numbers in sequence, sends location, switches on all lights and unlocks the front door for paramedics. Tested monthly." },
      { heading: "Smart locks with family fallback", body: "Fingerprint + face + mobile key + PIN + physical key. If your parent forgets a code, you can unlock the door remotely from anywhere in the world. Domestic help can be granted time-limited access (9am–1pm weekdays only)." },
      { heading: "Voice-first control", body: "Bigger buttons, simpler scenes, voice commands instead of apps. We program a 'Goodnight' scene that closes curtains, drops lights to 5%, locks the door and arms the security with a single phrase — 'Hey Google, Goodnight'." },
      { heading: "Discreet monitoring (with consent)", body: "Indoor cameras only where your parents agree. Door-open sensors and motion sensors are usually enough — they tell you mum has been up since 6:42am, made tea at 7:15, sat in the living room till 8:30. A reassuring digital pulse, not surveillance." },
      { heading: "What we typically install for ₹2–3 L", body: "Night-light automation, two smart locks (main door + service door), full AC control, curtain automation in master + living, video doorbell, 4-camera CCTV with elderly-mode (motion alerts only at unusual times), one Smart Touch panel as the 'family panel'." },
      { heading: "We do this with care", body: "Our installation team is trained for elderly homes. Slow, patient, explained twice. We onboard your parents personally and check in monthly for the first six months. Five-year warranty, lifetime support." },
    ], 12,
    "elderly smart home India, smart home for senior citizens India, fall prevention smart home, parents smart home India, dignity ageing in place India, smart home for old age India"),

  article("child-safe-smart-home-india", "for-homeowners",
    "Child-Safe Smart Home — A Parent's Complete Guide",
    "From toddler-proof outlets to teen screen-time controls — how a smart home actually protects every Indian family.",
    [
      { heading: "Childproofing has evolved", body: "Outlet covers and corner bumpers still matter — but in 2026, the most effective child safety in a premium home is digital. Locked rooms that aren't physically locked. Stoves that won't turn on alone. Pools that alarm when an unattended child approaches." },
      { heading: "Outlets that lock themselves", body: "Dynamic Series sockets can be set to 'child mode' from any panel — physically blocking power until released by a parent's PIN, fingerprint or app. Reverts automatically at 9pm so the night-light socket isn't blocked when you need it." },
      { heading: "Geyser and stove safety", body: "Geyser shuts off after 12 minutes unless a parent overrides. Smart shut-off valves on the kitchen gas. Stove burner timers. Real safety, invisible until needed." },
      { heading: "Room-locking without keys", body: "Study and home office doors with smart locks that the child can only open at specified hours. Lets you keep the home office unlocked during the day, secure after 9pm." },
      { heading: "Pool & balcony alarms", body: "Motion sensors on balcony doors and around pool perimeters. Discreet chime indoors when an unaccompanied child crosses. Five-second response time is the difference between a scare and a tragedy." },
      { heading: "Screen-time at the wall, not the device", body: "WiFi schedules at the router level mean iPads stop streaming at 9pm — no negotiation, no apps to uninstall. A single Dynamic Series key lets parents override for an hour of Sunday-evening cartoons. Teaches accountability, not policing." },
      { heading: "Sleep & study scenes", body: "Bedtime scene closes curtains, dims lights to 2%, locks doors, and silences screens. Study scene brightens lighting to 4000K, mutes home theatre, raises curtains, plays gentle focus music. Two buttons, two atmospheres." },
      { heading: "Parental peace of mind", body: "When the kids' bedroom door opens at 2am, the parent's panel glows softly. Front door opening sends a phone notification. Pool gate breach calls home immediately. Built once, lived with forever." },
    ], 11,
    "child safe smart home India, kid proof smart home India, child safety smart home, parental controls home automation India, pool safety smart home India, smart home for children India"),
];
